// src/contexts/MatchReadyContext.js
// ============================================================================
// MATCH LIFECYCLE OVERLAY - "One Stop Match Shop"
// 
// Global context that handles the ENTIRE match lifecycle:
// 
// PRE-MATCH FLOW:
// 1. Match locks → Show "Ready Up!" screen with countdown
// 2. User presses Ready → Show "Waiting for opponent..."
// 3. All players ready → Show "Verifying players..."
// 4. Verification complete → Show "🎯 CRIT TIME!" celebration (3 seconds)
// 5. Auto-dismiss → Players start playing
//
// POST-MATCH FLOW:
// 6. Match completed → Show result (WIN/LOSS)
// 7a. Winner: "VICTORY!" + Next Round or Champion modal
// 7b. Loser: Dispute timer + dispute options + Accept Result
// 8. User acknowledges → Dismiss or proceed to next match
// ============================================================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Platform,
  Animated,
  Easing,
  ScrollView,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  onSnapshot,
  doc,
  getDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

// ============================================================================
// CONSTANTS
// ============================================================================

// Modal phases (exported for the dev-only screenshot gallery)
export const PHASE = {
  // Pre-match phases
  READY_UP: 'ready_up',           // Waiting for user to press Ready
  WAITING_OTHERS: 'waiting',       // User ready, waiting for opponent(s)
  ALL_READY: 'all_ready',          // All ready, about to verify
  VERIFYING: 'verifying',          // Vision verification in progress
  VERIFICATION_FAILED: 'verification_failed', // Vision couldn't confirm — offer re-run
  CRIT_TIME: 'crit_time',          // 🎯 Match is live! Celebration screen
  REPORT_RESULT: 'report_result',  // Manual-results match live — players report when it ends

  // Post-match phases
  RESULT_WIN: 'result_win',        // User won - show victory screen
  RESULT_LOSS: 'result_loss',      // User lost - show dispute options
  UNDER_REVIEW: 'under_review',    // Outcome couldn't be auto-verified — manual review + claim flow
  CHAMPION: 'champion',            // User won the tournament!
  NEXT_ROUND: 'next_round',        // User advances - ready for next match
  
  // Terminal
  DISMISSED: 'dismissed',          // Modal dismissed
};

// How long to show the CRIT TIME screen (ms)
const CRIT_TIME_DURATION = 3500;

// How long to show the result screen before auto-dismissing (for winners)
const RESULT_AUTO_DISMISS_DURATION = 10000;

// Dispute reasons available to losers
const DISPUTE_REASONS = [
  { id: 'wrong_winner', label: 'Wrong winner detected', icon: 'close-circle', iconColor: '#F44336' },
  { id: 'technical_issue', label: 'Technical/connection issue', icon: 'cellular', iconColor: '#FF9800' },
  { id: 'cheating', label: 'Opponent cheating suspected', icon: 'game-controller', iconColor: '#9C27B0' },
  { id: 'stream_issue', label: 'Stream detection error', icon: 'tv', iconColor: '#2196F3' },
  { id: 'other', label: 'Other issue', icon: 'help-circle', iconColor: '#888' },
];

// ============================================================================
// CONTEXT
// ============================================================================

const MatchReadyContext = createContext({
  pendingMatch: null,
  isReady: false,
  phase: null,
  matchResult: null,
});

export const useMatchReady = () => useContext(MatchReadyContext);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

// mockState (dev-only, used by DevGalleryScreen): { phase, pendingMatch,
// readyStatus, matchResult, user } — when present, every auth/Firestore
// listener below is disabled and modal state comes straight from the caller.
// Production mounts pass no mockState, so this is inert there.
export function MatchReadyProvider({ children, mockState }) {
  const [user, setUser] = useState(null);
  const [pendingMatch, setPendingMatch] = useState(null);
  const [readyStatus, setReadyStatus] = useState(null);
  const [phase, setPhase] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Match result state
  const [matchResult, setMatchResult] = useState(null);
  const [disputeTimeLeft, setDisputeTimeLeft] = useState(null);
  const [selectedDisputeReason, setSelectedDisputeReason] = useState(null);
  const [disputeDetails, setDisputeDetails] = useState('');
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  
  // Result reporting state (manual-results games — no vision resolution)
  const [reportChoice, setReportChoice] = useState(null);      // 'win' | 'loss'
  const [reportPosition, setReportPosition] = useState('');    // grid finish (text input)
  const [reportDnf, setReportDnf] = useState(false);
  const [reportEditing, setReportEditing] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);            // parent event doc snapshot (subdoc matches)

  // Host rating state (Phase 5)
  const [hostRatings, setHostRatings] = useState({});
  const [hostRatingFeedback, setHostRatingFeedback] = useState('');
  const [hostRatingSubmitted, setHostRatingSubmitted] = useState(false);
  const [hostRatingSubmitting, setHostRatingSubmitting] = useState(false);
  
  // Animation values
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const critTimeScale = useRef(new Animated.Value(0)).current;
  const critTimeOpacity = useRef(new Animated.Value(0)).current;
  const glowAnim = useRef(new Animated.Value(0)).current;
  const resultScale = useRef(new Animated.Value(0)).current;
  const resultOpacity = useRef(new Animated.Value(0)).current;
  const confettiAnim = useRef(new Animated.Value(0)).current;
  
  // Loading dots animation values
  const dot1Anim = useRef(new Animated.Value(0.4)).current;
  const dot2Anim = useRef(new Animated.Value(0.4)).current;
  const dot3Anim = useRef(new Animated.Value(0.4)).current;
  
  const currentMatchPathRef = useRef(null);
  const matchListenerRef = useRef(null);
  const modalShownForMatchRef = useRef(null);
  // Per-match acknowledgement (audit H7): the old single hasAcknowledged
  // boolean was never reset, so acknowledging ONE result suppressed every
  // later result (incl. a loser's dispute window) for the whole session.
  const acknowledgedMatchesRef = useRef(new Set());
  // Identity of the last recentMatchResult shown — the user-doc snapshot
  // fires on every unrelated write (Crit balance etc.); without this the
  // result modal re-took the screen (even over an active ready countdown).
  const shownRecentResultRef = useRef(null);
  // Fresh updatePhase for the long-lived nested match listener (audit H8):
  // it captured a stale closure whose `phase` was null, replaying the CRIT
  // TIME overlay + fanfare mid-game on every match-doc write.
  const updatePhaseRef = useRef(null);
  // path|status|readyStatus of the last handled snapshot — skip phase work
  // on writes that don't change what the user should see.
  const lastMatchStatusRef = useRef(null);
  // Whether the current match resolves from PLAYER REPORTS (manualResults —
  // community games + catalog games without vision). Ref, not state: read
  // inside the CRIT TIME dismiss timeout without re-binding the callback.
  const manualResultsRef = useRef(false);
  const eventInfoRef = useRef(null);
  // Current phase, readable inside async callbacks (the eventInfo fetch can
  // land after the CRIT TIME dismiss timer already ran).
  const phaseRef = useRef(null);

  // Timeout refs
  const critTimeoutRef = useRef(null);
  const resultTimeoutRef = useRef(null);

  // Event-level FFA (showdown) results resolve ASYNC — the position and Crits
  // live on the caller's own entry doc, not the event doc. The completed EVENT
  // doc keeps taking writes (payout latch, floods, viewer counts) and each one
  // re-runs handleCompletedMatch: the seq drops stale fetches, the presented
  // ref lets re-runs refresh the numbers without replaying the fanfare.
  const eventFfaResultSeqRef = useRef(0);
  const resultPresentedRef = useRef(null);

  const db = getFirestore();
  const functions = getFunctions();
  const auth = getAuth();

  // ============================================================================
  // AUTH LISTENER
  // ============================================================================
  
  useEffect(() => {
    if (mockState) return;
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setPendingMatch(null);
        setReadyStatus(null);
        setPhase(null);
        setMatchResult(null);
        // Full session-state reset so a different account on the same device
        // doesn't inherit acknowledgements/shown-result suppression (audit H7).
        setHasAcknowledged(false);
        acknowledgedMatchesRef.current = new Set();
        shownRecentResultRef.current = null;
        modalShownForMatchRef.current = null;
        lastMatchStatusRef.current = null;
      }
    });
    return () => unsubscribe();
  }, [auth]);

  // ============================================================================
  // SOUND EFFECTS
  // ============================================================================
  
  const playSound = useCallback((soundType) => {
    if (Platform.OS !== 'web') return; // Only web for now
    
    try {
      // Create audio context for sound effects
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      
      const audioCtx = new AudioContext();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      switch (soundType) {
        case 'ready_up':
          // Urgent alert: three rising tones
          oscillator.frequency.setValueAtTime(440, audioCtx.currentTime);
          oscillator.frequency.setValueAtTime(554, audioCtx.currentTime + 0.15);
          oscillator.frequency.setValueAtTime(659, audioCtx.currentTime + 0.3);
          gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);
          oscillator.start(audioCtx.currentTime);
          oscillator.stop(audioCtx.currentTime + 0.5);
          break;
          
        case 'crit_time':
          // Victory fanfare: ascending triumphant tones
          oscillator.frequency.setValueAtTime(523, audioCtx.currentTime);
          oscillator.frequency.setValueAtTime(659, audioCtx.currentTime + 0.1);
          oscillator.frequency.setValueAtTime(784, audioCtx.currentTime + 0.2);
          oscillator.frequency.setValueAtTime(1047, audioCtx.currentTime + 0.3);
          gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.6);
          oscillator.start(audioCtx.currentTime);
          oscillator.stop(audioCtx.currentTime + 0.6);
          break;
          
        case 'win':
          // Victory sound: major chord arpeggio
          oscillator.frequency.setValueAtTime(523, audioCtx.currentTime);
          oscillator.frequency.setValueAtTime(659, audioCtx.currentTime + 0.15);
          oscillator.frequency.setValueAtTime(784, audioCtx.currentTime + 0.3);
          oscillator.frequency.setValueAtTime(1047, audioCtx.currentTime + 0.45);
          gainNode.gain.setValueAtTime(0.4, audioCtx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.8);
          oscillator.start(audioCtx.currentTime);
          oscillator.stop(audioCtx.currentTime + 0.8);
          break;
          
        case 'loss':
          // Somber tone: descending minor
          oscillator.frequency.setValueAtTime(392, audioCtx.currentTime);
          oscillator.frequency.setValueAtTime(349, audioCtx.currentTime + 0.2);
          oscillator.frequency.setValueAtTime(330, audioCtx.currentTime + 0.4);
          gainNode.gain.setValueAtTime(0.25, audioCtx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.6);
          oscillator.start(audioCtx.currentTime);
          oscillator.stop(audioCtx.currentTime + 0.6);
          break;
          
        case 'champion':
          // Epic victory: full fanfare
          oscillator.frequency.setValueAtTime(523, audioCtx.currentTime);
          oscillator.frequency.setValueAtTime(659, audioCtx.currentTime + 0.1);
          oscillator.frequency.setValueAtTime(784, audioCtx.currentTime + 0.2);
          oscillator.frequency.setValueAtTime(1047, audioCtx.currentTime + 0.3);
          oscillator.frequency.setValueAtTime(1318, audioCtx.currentTime + 0.5);
          gainNode.gain.setValueAtTime(0.4, audioCtx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 1.0);
          oscillator.start(audioCtx.currentTime);
          oscillator.stop(audioCtx.currentTime + 1.0);
          break;
      }
    } catch (e) {
      console.log('[MatchReady] Sound playback failed:', e);
    }
  }, []);

  // ============================================================================
  // LISTEN FOR MATCHES (READY-UP + COMPLETED)
  // ============================================================================
  
  useEffect(() => {
    if (mockState) return;
    if (!user?.uid) return;

    const unsubscribes = [];

    // All events live under `tournaments`; a participant's current match is
    // surfaced via users/{uid}.activeMatchPath (set by the ready system). The
    // legacy top-level `matches` collection is deprecated — the standalone
    // onSnapshot query that used to live here (and replayed completed-match
    // win/loss modals on every load) has been removed.

    // Listen to user's document for activeMatchPath (for tournament matches)
    const userDocRef = doc(db, 'users', user.uid);
    unsubscribes.push(
      onSnapshot(userDocRef, (docSnap) => {
        const userData = docSnap.data();
        
        if (userData?.activeMatchPath) {
          // FIXED: Check if this is the same match we're already listening to
          if (currentMatchPathRef.current === userData.activeMatchPath) {
            // Same match, no need to create new listener
            return;
          }
          
          // FIXED: Clean up previous match listener before creating new one
          if (matchListenerRef.current) {
            matchListenerRef.current();
            matchListenerRef.current = null;
          }
          
          // Update the current match path ref
          currentMatchPathRef.current = userData.activeMatchPath;
          
          // Listen to this specific match
          const matchRef = doc(db, userData.activeMatchPath);
          const unsubMatch = onSnapshot(matchRef, (matchSnap) => {
            if (!matchSnap.exists()) return;
            
            const match = {
              id: matchSnap.id,
              path: userData.activeMatchPath,
              ...matchSnap.data(),
            };
            
            // Handle completed matches
            if (match.status === 'completed') {
              handleCompletedMatch(match, user.uid);
              return;
            }
            
            // FIXED: Track if we've already shown modal for this specific match
            const isNewMatch = modalShownForMatchRef.current !== match.path;
            
            // Show modal for:
            // - locked matches (ready up phase)
            // - live matches (CRIT TIME!) regardless of how they went live
            // Don't show if timed out or cancelled
            // Don't show if GT7 host hasn't submitted the Room ID yet — racers
            // have nothing to join, and the 5-min ready window shouldn't start
            // until the room actually exists.
            if ((match.status === 'locked' || match.status === 'live') &&
                match.readyStatus !== 'timeout' &&
                match.status !== 'cancelled' &&
                match.awaitingRoomId !== true) {

              // Only run phase work when the VISIBLE state changed (new match
              // or status/readyStatus transition). Backend writes unrelated
              // fields to live match docs constantly; combined with the stale
              // closure this used to replay the full-screen CRIT TIME overlay
              // mid-game on every such write (audit H8).
              const statusKey = `${match.path}|${match.status}|${match.readyStatus || ''}`;
              const statusChanged = lastMatchStatusRef.current !== statusKey;
              lastMatchStatusRef.current = statusKey;

              modalShownForMatchRef.current = match.path;
              setPendingMatch(match);
              setReadyStatus(match);
              if (isNewMatch || statusChanged) {
                setIsMinimized(false);
                setError(null);
                (updatePhaseRef.current || updatePhase)(match, user.uid);
              }

              // FIXED: Only play sound for NEW match, not updates to same match
              if (isNewMatch && match.status === 'locked') {
                playSound('ready_up');
              }
            }
          });
          
          // Store the unsubscribe function for cleanup
          matchListenerRef.current = unsubMatch;
          unsubscribes.push(() => {
            if (matchListenerRef.current) {
              matchListenerRef.current();
              matchListenerRef.current = null;
            }
          });
        } else {
          // activeMatchPath was cleared - clean up listener
          if (matchListenerRef.current) {
            matchListenerRef.current();
            matchListenerRef.current = null;
          }
          currentMatchPathRef.current = null;
        }
        
        // Also check for recentMatchResult to show results. Guarded by
        // RESULT IDENTITY, not a session boolean: user-doc snapshots fire on
        // every unrelated write, and the old guard both re-showed the same
        // result over an active ready countdown AND (via the never-reset
        // boolean) suppressed all later results (audits H7/M6).
        if (userData?.recentMatchResult) {
          const result = userData.recentMatchResult;
          const resultKey = `${result.matchId || result.matchPath || ''}:${result.completedAt?.toMillis?.() || result.completedAt || ''}`;
          const alreadyShown = shownRecentResultRef.current === resultKey;
          const acked = result.matchPath && acknowledgedMatchesRef.current.has(result.matchPath);
          const resultTime = result.completedAt?.toDate?.() || new Date(result.completedAt);
          const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
          if (!alreadyShown && !acked && resultTime > hourAgo) {
            shownRecentResultRef.current = resultKey;
            setMatchResult(result);
            setPhase(result.outcome === 'win' ? PHASE.RESULT_WIN : PHASE.RESULT_LOSS);
            triggerResultAnimation(result.outcome === 'win');
          }
        }
      }, (err) => {
        console.error('[MatchReady] User doc query error:', err);
      })
    );

    return () => {
      // Clean up all listeners
      unsubscribes.forEach(unsub => {
        if (typeof unsub === 'function') unsub();
      });
      // Also clean up match listener ref
      if (matchListenerRef.current) {
        matchListenerRef.current();
        matchListenerRef.current = null;
      }
      currentMatchPathRef.current = null;
    };
  }, [user?.uid, db, playSound]);

  // ============================================================================
  // HANDLE COMPLETED MATCH
  // ============================================================================
  
  const handleCompletedMatch = useCallback((match, uid) => {
    // Don't show if THIS match was already acknowledged (per-match, audit H7 —
    // a session-wide boolean here suppressed every later result).
    if (acknowledgedMatchesRef.current.has(match.path)) return;
    
    // CRITICAL: Cancel any pending CRIT_TIME auto-dismiss
    // This prevents the result modal from being dismissed
    if (critTimeoutRef.current) {
      clearTimeout(critTimeoutRef.current);
      critTimeoutRef.current = null;
      console.log('[MatchReady] Cancelled CRIT_TIME timeout for result display');
    }
    
    // Multi-racer HEAT: the result is a finishing position + advance/eliminate,
    // not a 1v1 win/loss — the pairwise copy below ("Defeated X" / "X won this
    // round") told a paid P4 finisher "Better luck next time". Heat docs carry
    // heatPlacements + advancers (applyHeatResolution) and never winnerId.
    const isHeatMatch = match.matchType === 'heat'
      || (Array.isArray(match.heatPlacements) && match.heatPlacements.length > 0);
    let racingFields = null;
    if (isHeatMatch) {
      const mine = (match.heatPlacements || []).find((p) => p.userId === uid);
      // Ready-timeout DQs are EXCLUDED from heatPlacements by the resolvers —
      // without this check a DQ'd racer with the app open rendered a literal
      // "P? — eliminated this round". Accept both playersDisqualified key
      // shapes (legacy 'odisplayName' + 'userId'), like every other reader.
      const racingDq = (match.playersDisqualified || [])
        .map((d) => (typeof d === 'string' ? d : (d?.userId || d?.odisplayName)))
        .includes(uid);
      const racingPosition = mine && !mine.dnf ? Number(mine.position) : null;
      const racingAdvanced = Array.isArray(match.advancers) && match.advancers.includes(uid);
      const racingFinals = match.isFinalMatch === true
        || (match.roundNumber != null && match.totalRounds != null && match.roundNumber === match.totalRounds);
      // First place below the cut isn't definitively out: the round merge
      // pulls the fastest of those across all heats into any open next-round
      // seat (checkHeatsRoundCompletion). Hedge the copy for exactly that
      // position instead of asserting an elimination that may reverse.
      const heatCutoff = Number(match.advanceCount);
      const racingOnBubble = !racingFinals && !racingAdvanced
        && Number.isInteger(heatCutoff) && heatCutoff > 0
        && !!mine && !mine.dnf && Number(mine.position) === heatCutoff + 1;
      racingFields = {
        racing: true,
        racingPosition,
        racingDnf: !!mine?.dnf,
        racingDq,
        racingOnBubble,
        racingAdvanced,
        racingFinals,
      };
    }

    // Build + surface the result modal. Split out so the event-level FFA
    // branch below can await its entry fetch first; every other path calls it
    // synchronously, exactly as before.
    const presentResult = (racingFields, oncePerPath = false) => {
      // Determine if user won or lost
      const isWinner = racingFields
        ? (racingFields.racingShowdown
          ? match.championId === uid
          : (racingFields.racingFinals ? racingFields.racingPosition === 1 : racingFields.racingAdvanced))
        : (match.winnerUid === uid ||
           match.winners?.includes(uid) ||
           match.winnerId === uid);

      // Both fields must EXIST — `undefined === undefined` made every
      // single-match (H2H) winner a "Tournament CHAMPION" (audit C6 tail).
      const isFinalMatch = racingFields
        ? racingFields.racingFinals
        : (match.isFinalMatch === true ||
          (match.roundNumber != null && match.totalRounds != null && match.roundNumber === match.totalRounds));

      const result = {
        ...(racingFields || {}),
        matchId: match.id,
        matchPath: match.path,
        outcome: isWinner ? 'win' : 'loss',
        isWinner,
        isFinalMatch,
        // Compare by the participant's uid fields — the old `odisplayName`
        // predicate matched a nonexistent field, always returning
        // participants[0] ("Defeated <your own name>", audit M7).
        // Bracket match docs carry opponents as p1/p2 slots (ign, not
        // displayName) — the participants array is the tournament-doc shape and
        // is absent here, which rendered "Defeated Opponent" (2026-07-05 test).
        opponentName: (() => {
          const slotUid = (p) => p && (p.userId || p.uid || p.id);
          const opp = [match.p1, match.p2].find((p) => slotUid(p) && slotUid(p) !== uid);
          return opp?.ign || opp?.displayName || opp?.userName
            || match.participants?.find(p => (p.userId || p.uid || p.odUserId) !== uid)?.displayName
            || 'Opponent';
        })(),
        gameName: match.gameName || match.game,
        eventName: match.eventName || match.tournamentName,
        eventId: match.eventId || match.tournamentId,
        gameId: match.game || match.gameId,
        inviteMode: match.inviteMode || null,
        hostUid: match.hostUid || match.hostId || null,
        // Bracket docs use `round`; `roundNumber` is a different shape. Never
        // render "Round undefined" — fall through to 'Match'.
        roundName: match.roundName
          || (match.roundNumber != null ? `Round ${match.roundNumber}`
            : (match.round != null ? `Round ${match.round}` : 'Match')),
        prizeAmount: isWinner ? (match.prizeAmount || match.payoutAmount) : 0,
        disputeWindowEndsAt: match.disputeWindowEndsAt,
        disputeReasons: DISPUTE_REASONS,
        nextMatchPath: match.nextMatchPath,
        completedAt: match.completedAt || new Date(),
      };

      setMatchResult(result);
      setPendingMatch(match);
      setReadyStatus(match);

      // Event docs keep taking writes after completion and each re-runs this
      // handler — the numbers above refresh (payoutReceived stamps seconds
      // after the status flip), but the fanfare plays once per match.
      if (oncePerPath) {
        if (resultPresentedRef.current === match.path) return;
        resultPresentedRef.current = match.path;
      }

      if (isWinner) {
        setPhase(isFinalMatch ? PHASE.CHAMPION : PHASE.RESULT_WIN);
        playSound(isFinalMatch ? 'champion' : 'win');
      } else {
        setPhase(PHASE.RESULT_LOSS);
        playSound('loss');
      }

      triggerResultAnimation(isWinner);

      // Vibrate
      if (Platform.OS !== 'web' && typeof navigator !== 'undefined' && navigator.vibrate) {
        if (isWinner) {
          navigator.vibrate([0, 100, 50, 100, 50, 100, 50, 200]);
        } else {
          navigator.vibrate([0, 300, 100, 300]);
        }
      }
    };

    // Event-level FFA completion (16-racer showdowns, battle royales): the
    // "match" is the EVENT doc — no winnerId, no opponent, and the pairwise
    // copy told a P4 showdown finisher "Better luck next time — <arbitrary
    // name> won this round". Champion comes from championId; the finishing
    // position and Crits live on the caller's OWN entry doc
    // (entries/{uid}.finalRank / payoutReceived), so fetch before saying
    // anything — everyone gets their real placement, never a loss line over
    // a paid finish.
    const isEventFfaDoc = !isHeatMatch && !match.p1 && !match.p2
      && match.championId != null
      && ['showdown', 'racing'].includes(String(match.mode || '').toLowerCase());
    if (isEventFfaDoc) {
      const seq = ++eventFfaResultSeqRef.current;
      (async () => {
        let entry = null;
        try {
          const entrySnap = await getDoc(doc(db, match.path, 'entries', uid));
          if (entrySnap.exists()) entry = entrySnap.data();
        } catch (e) {
          console.log('[MatchReady] Showdown entry fetch failed:', e?.message || e);
        }

        const racingPosition = Number(entry?.finalRank) || Number(entry?.placement) || null;
        // payoutReceived is the SAME field for both currencies — whole Crits
        // on a Crit event, CENTS on a cash one — so never surface it without
        // this branch (and money never appears in user copy anyway).
        const isCritEvent = match.entryCurrency === 'crits' || match.critEntryCost != null;
        const payoutRaw = Number(entry?.payoutReceived);
        const critsReceived = isCritEvent && Number.isFinite(payoutRaw) && payoutRaw > 0
          ? Math.round(payoutRaw)
          : null;

        // Mirror the server's deriveOutcome: a rank wins over any stale quit
        // wording (adminResolveRace DELETES finalRank on a real DNF), and the
        // opponent's half of a vision pattern must not read as OUR quit.
        const ownSignals = [entry?.matchOutcome, entry?.matchStatus, entry?.visionOutcome, entry?.visionPattern]
          .map((v) => String(v || '').toLowerCase())
          .join(' ')
          .replace(/opponent_[a-z]+/g, '');
        const racingDnf = !racingPosition && /rage_quit|dnf|disconnect|quit|abandon/.test(ownSignals);

        // Crowd boost (Rumble): every RANKED finisher shares it. The split
        // (critsEach) lives in rumbleDistributions, which has no client rules
        // — getEventCeremony is the audience's only data path to it. Same
        // trust gate as the ceremony: split on the books AND pays at least 1.
        let critsBoost = null;
        if (racingPosition && Number(match.floodTotalCents) > 0) {
          try {
            const getCeremony = httpsCallable(functions, 'getEventCeremony');
            const ceremony = await getCeremony({ eventId: match.id });
            const each = Number(ceremony?.data?.rumbleCritsEach);
            if (ceremony?.data?.resultsFinal && each > 0) critsBoost = Math.floor(each);
          } catch (e) {
            console.log('[MatchReady] Rumble split fetch failed:', e?.message || e);
          }
        }

        // Rumbles (evergreen 3.3) are cash, credited to the players once the
        // results are final, so this modal never shows a figure or a share:
        // a ranked finisher is told the crowd boosted the race, after results
        // are final (the same predicate getEventCeremony uses). The count is
        // off the event doc itself (floodCount + rumbleCount). A legacy Crit
        // split that renders above takes precedence.
        const crowdCount = (Number(match.rumbleCount) || 0) + (Number(match.floodCount) || 0);
        const eventResultsFinal = match.status === 'completed'
          && (match.critPayoutsCompleted === true || match.payoutsCompleted === true);
        const crowdBoosted = !!racingPosition && crowdCount > 0 && eventResultsFinal
          && !(critsBoost > 0 && critsReceived > 0);

        // Only the LATEST fetch may touch state (completed event docs keep
        // getting written, re-running this handler), and never after the
        // user already acknowledged this result.
        if (seq !== eventFfaResultSeqRef.current) return;
        if (acknowledgedMatchesRef.current.has(match.path)) return;

        // Same no-show detection as the heat branch — accept both
        // playersDisqualified key shapes.
        const racingDq = (match.playersDisqualified || [])
          .map((d) => (typeof d === 'string' ? d : (d?.userId || d?.odisplayName)))
          .includes(uid);

        presentResult({
          racing: true,
          racingShowdown: true,
          racingPosition,
          racingDnf,
          racingDq,
          racingOnBubble: false,
          racingAdvanced: false,
          racingFinals: true,
          critsReceived,
          critsBoost,
          crowdBoosted,
        }, true);
      })();
      return;
    }

    presentResult(racingFields);
  }, [playSound]);

  // ============================================================================
  // DETERMINE PHASE BASED ON MATCH STATE
  // ============================================================================
  
  const updatePhase = useCallback((match, uid) => {
    if (!match) {
      setPhase(null);
      return;
    }
    
    const userReady = match.playersReadyState?.[uid]?.ready;
    const readyCount = match.playersReady || 0;
    const totalPlayers = match.totalPlayersRequired || 0;
    const allReady = readyCount >= totalPlayers && totalPlayers > 0;
    
    // Match completed - show result
    if (match.status === 'completed') {
      // Handled by handleCompletedMatch
      return;
    }
    
    // Outcome couldn't be auto-verified (match stays 'live' underneath while a
    // human reviews) — or a dispute is already in flight. Surface the review
    // state + claim flow. Without this, the review only ever reached players
    // by email, and a 'disputed' status fell through to a bogus waiting phase.
    if ((match.status === 'live' && match.visionRequiresReview && !manualResultsRef.current)
      || match.status === 'disputed') {
      if (phase !== PHASE.UNDER_REVIEW && phase !== PHASE.DISMISSED) {
        setPhase(PHASE.UNDER_REVIEW);
      }
      return;
    }

    // Match went live - CRIT TIME! Manual-results matches hand off to the
    // report panel after the celebration (see triggerCritTimeAnimation), and
    // every later match-doc write (including our own report) must NOT replay
    // the fanfare.
    if (match.status === 'live') {
      if (phase !== PHASE.CRIT_TIME && phase !== PHASE.DISMISSED && phase !== PHASE.REPORT_RESULT) {
        setPhase(PHASE.CRIT_TIME);
        triggerCritTimeAnimation();
      }
      return;
    }
    
    // Verification couldn't confirm the matchup screen — offer a re-run
    // (previously this fell through to WAITING_OTHERS and looked like being
    // stuck on "verifying" forever).
    if (match.readyStatus === 'verification_failed' && match.status === 'locked') {
      setPhase(PHASE.VERIFICATION_FAILED);
      return;
    }

    // Verification in progress
    if (match.readyStatus === 'verifying') {
      setPhase(PHASE.VERIFYING);
      return;
    }
    
    // All players ready, waiting for verification
    if (match.readyStatus === 'all_ready' || allReady) {
      setPhase(PHASE.ALL_READY);
      return;
    }
    
    // User has readied, waiting for others
    if (userReady) {
      setPhase(PHASE.WAITING_OTHERS);
      return;
    }
    
    // Default: waiting for user to ready up
    setPhase(PHASE.READY_UP);
  }, [phase]);

  // Keep a fresh reference for long-lived listeners (audit H8): the nested
  // match listener outlives many phase changes, and calling a captured
  // updatePhase replayed CRIT TIME with a stale `phase` of null.
  useEffect(() => { updatePhaseRef.current = updatePhase; }, [updatePhase]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // ============================================================================
  // REAL-TIME UPDATES TO CURRENT PENDING MATCH
  // ============================================================================
  
  // Parent event info for bracket sub-matches: manualResults / game / mode
  // live on the EVENT doc, not the match doc. One fetch per match path.
  useEffect(() => {
    if (mockState) return;
    setEventInfo(null);
    setReportChoice(null);
    setReportPosition('');
    setReportDnf(false);
    setReportEditing(false);
    setReportError(null);
    const parts = (pendingMatch?.path || '').split('/');
    if (parts.length !== 4 || parts[0] !== 'tournaments') return;
    let cancelled = false;
    getDoc(doc(db, `tournaments/${parts[1]}`))
      .then((snap) => {
        if (!cancelled && snap.exists()) {
          const info = { id: snap.id, ...snap.data() };
          setEventInfo(info);
          eventInfoRef.current = info;
          // The fetch can land after the first match snapshots — refresh the
          // manual-results flag so the CRIT TIME handoff sees it. Heats (mode
          // 'racing') never take manual reports (server refuses them too).
          if (info.manualResults === true && String(info.mode || '').toLowerCase() !== 'racing') {
            manualResultsRef.current = true;
            // If the CRIT TIME dismiss timer already ran (fetch lost the
            // race), restore the report chip instead of leaving the match
            // unreportable.
            if (phaseRef.current === PHASE.DISMISSED) {
              setPhase(PHASE.REPORT_RESULT);
              setIsMinimized(true);
            }
          }
        }
      })
      .catch(() => { /* report panel just won't show — vision/admin still resolve */ });
    return () => { cancelled = true; eventInfoRef.current = null; };
  }, [pendingMatch?.path, db, mockState]);

  // A stale MATCH ENDED error must not bleed into the result pickers once the
  // match flips to ended (possibly via the other player's tap). Keyed on the
  // millis value — the Timestamp object identity changes every snapshot.
  const playerEndedAtMs = readyStatus?.playerEndedAt?.toMillis?.() || 0;
  useEffect(() => { setReportError(null); }, [playerEndedAtMs]);

  useEffect(() => {
    if (mockState) return;
    if (!pendingMatch?.path || !user?.uid) return;

    const matchRef = doc(db, pendingMatch.path);
    const unsub = onSnapshot(matchRef, (snap) => {
      if (!snap.exists()) {
        setPendingMatch(null);
        setPhase(null);
        return;
      }

      const data = snap.data();
      setReadyStatus(data);
      // Event-doc matches (showdown grids) carry manualResults themselves;
      // bracket sub-matches inherit it from the fetched parent event. Heats
      // (mode 'racing') are EXCLUDED — they score from the full finishing
      // order and have no manual-report path yet (server refuses them too).
      const parentMode = String(eventInfoRef.current?.mode || data.mode || '').toLowerCase();
      manualResultsRef.current = parentMode !== 'racing'
        && (data.manualResults === true
          || (pendingMatch.path.split('/').length === 4 && eventInfoRef.current?.manualResults === true));
      
      // Handle completed
      if (data.status === 'completed') {
        handleCompletedMatch({ ...data, path: pendingMatch.path, id: snap.id }, user.uid);
        return;
      }
      
      updatePhase({ ...data, path: pendingMatch.path }, user.uid);
      
      // Only dismiss if timed out or cancelled
      if (data.readyStatus === 'timeout' || data.status === 'cancelled') {
        setPendingMatch(null);
        setPhase(null);
      }
    });

    return () => unsub();
  }, [pendingMatch?.path, user?.uid, db, updatePhase, handleCompletedMatch]);

  // ============================================================================
  // DISPUTE WINDOW COUNTDOWN
  // ============================================================================
  
  useEffect(() => {
    if (!matchResult?.disputeWindowEndsAt || phase !== PHASE.RESULT_LOSS) {
      setDisputeTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      const deadline = matchResult.disputeWindowEndsAt.toDate 
        ? matchResult.disputeWindowEndsAt.toDate() 
        : new Date(matchResult.disputeWindowEndsAt);
      const now = new Date();
      return Math.max(0, Math.floor((deadline - now) / 1000));
    };

    setDisputeTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      const remaining = calculateTimeLeft();
      setDisputeTimeLeft(remaining);

      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [matchResult?.disputeWindowEndsAt, phase]);

  // ============================================================================
  // CRIT TIME ANIMATION
  // ============================================================================
  
  const triggerCritTimeAnimation = useCallback(() => {
    critTimeScale.setValue(0);
    critTimeOpacity.setValue(0);
    glowAnim.setValue(0);
    
    // Play CRIT TIME sound
    playSound('crit_time');
    
    if (Platform.OS !== 'web' && typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([0, 100, 50, 100, 50, 200]);
    }
    
    Animated.sequence([
      Animated.parallel([
        Animated.spring(critTimeScale, {
          toValue: 1,
          friction: 4,
          tension: 100,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(critTimeOpacity, {
          toValue: 1,
          duration: 200,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]),
      Animated.loop(
        Animated.sequence([
          Animated.timing(glowAnim, {
            toValue: 1,
            duration: 500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(glowAnim, {
            toValue: 0,
            duration: 500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: Platform.OS !== 'web',
          }),
        ]),
        { iterations: 3 }
      ),
    ]).start();
    
    if (critTimeoutRef.current) {
      clearTimeout(critTimeoutRef.current);
    }
    critTimeoutRef.current = setTimeout(() => {
      if (manualResultsRef.current) {
        // Manual-results match: keep the match attached and drop to a
        // minimized "report your result" chip — the player is mid-game.
        setPhase(PHASE.REPORT_RESULT);
        setIsMinimized(true);
      } else {
        setPhase(PHASE.DISMISSED);
        // Sub-match whose parent-event fetch hasn't landed yet: keep the
        // match attached so a late manualResults=true can still restore the
        // report chip (see the eventInfo fetch). Everything else detaches.
        const parts = String(currentMatchPathRef.current || '').split('/');
        const awaitingEventInfo = parts.length === 4 && !eventInfoRef.current;
        if (!awaitingEventInfo) setPendingMatch(null);
      }
    }, CRIT_TIME_DURATION);
  }, [critTimeScale, critTimeOpacity, glowAnim, playSound]);

  // ============================================================================
  // RESULT ANIMATION
  // ============================================================================
  
  const triggerResultAnimation = useCallback((isWinner) => {
    resultScale.setValue(0);
    resultOpacity.setValue(0);
    confettiAnim.setValue(0);
    
    Animated.parallel([
      Animated.spring(resultScale, {
        toValue: 1,
        friction: 4,
        tension: 80,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(resultOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: Platform.OS !== 'web',
      }),
      isWinner && Animated.loop(
        Animated.timing(confettiAnim, {
          toValue: 1,
          duration: 2000,
          useNativeDriver: Platform.OS !== 'web',
        }),
        { iterations: 3 }
      ),
    ].filter(Boolean)).start();
  }, [resultScale, resultOpacity, confettiAnim]);

  // ============================================================================
  // DEV-ONLY MOCK INJECTION (screenshot gallery)
  // ============================================================================

  useEffect(() => {
    if (!mockState) return;
    setUser(mockState.user || { uid: 'mock-user' });
    setPendingMatch(mockState.pendingMatch || null);
    setReadyStatus(mockState.readyStatus || mockState.pendingMatch || null);
    setMatchResult(mockState.matchResult || null);
    setIsMinimized(false);
    setError(null);
    setPhase(mockState.phase || null);
    if (mockState.phase === PHASE.CRIT_TIME) {
      triggerCritTimeAnimation();
      // Hold the celebration on screen — no auto-dismiss while mocked.
      if (critTimeoutRef.current) {
        clearTimeout(critTimeoutRef.current);
        critTimeoutRef.current = null;
      }
    } else if ([PHASE.RESULT_WIN, PHASE.RESULT_LOSS, PHASE.CHAMPION, PHASE.NEXT_ROUND].includes(mockState.phase)) {
      triggerResultAnimation(mockState.phase !== PHASE.RESULT_LOSS);
    }
  }, [mockState, triggerCritTimeAnimation, triggerResultAnimation]);

  // ============================================================================
  // LOADING DOTS ANIMATION
  // ============================================================================
  
  useEffect(() => {
    const shouldAnimate = phase === PHASE.VERIFYING || phase === PHASE.ALL_READY || phase === PHASE.WAITING_OTHERS;
    
    if (!shouldAnimate) {
      dot1Anim.setValue(0.4);
      dot2Anim.setValue(0.4);
      dot3Anim.setValue(0.4);
      return;
    }
    
    const createDotAnimation = (dotAnim, delay) => {
      return Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(dotAnim, {
            toValue: 1,
            duration: 300,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(dotAnim, {
            toValue: 0.4,
            duration: 300,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.delay(600 - delay),
        ])
      );
    };
    
    const dot1Animation = createDotAnimation(dot1Anim, 0);
    const dot2Animation = createDotAnimation(dot2Anim, 200);
    const dot3Animation = createDotAnimation(dot3Anim, 400);
    
    dot1Animation.start();
    dot2Animation.start();
    dot3Animation.start();
    
    return () => {
      dot1Animation.stop();
      dot2Animation.stop();
      dot3Animation.stop();
    };
  }, [phase, dot1Anim, dot2Anim, dot3Anim]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (critTimeoutRef.current) clearTimeout(critTimeoutRef.current);
      if (resultTimeoutRef.current) clearTimeout(resultTimeoutRef.current);
    };
  }, []);

  // ============================================================================
  // COUNTDOWN TIMER (Ready Up)
  // ============================================================================
  
  useEffect(() => {
    if (!pendingMatch?.readyDeadline) {
      setTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      const deadline = pendingMatch.readyDeadline.toDate 
        ? pendingMatch.readyDeadline.toDate() 
        : new Date(pendingMatch.readyDeadline);
      const now = new Date();
      return Math.max(0, Math.floor((deadline - now) / 1000));
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [pendingMatch?.readyDeadline]);

  // ============================================================================
  // PULSE ANIMATION FOR URGENT STATE
  // ============================================================================
  
  useEffect(() => {
    if (timeLeft !== null && timeLeft < 60 && timeLeft > 0 && phase === PHASE.READY_UP) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.05,
            duration: 500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: Platform.OS !== 'web',
          }),
        ])
      );
      pulse.start();
      return () => pulse.stop();
    }
  }, [timeLeft, pulseAnim, phase]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  const handleReady = useCallback(async () => {
    if (!pendingMatch?.path || isSubmitting) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const confirmReady = httpsCallable(functions, 'confirmReady');
      const result = await confirmReady({ matchPath: pendingMatch.path });
      console.log('[MatchReady] Ready confirmed:', result.data);
    } catch (err) {
      console.error('[MatchReady] Ready failed:', err);
      setError(err.message || 'Failed to confirm ready. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [pendingMatch?.path, functions, isSubmitting]);

  // Madden detection for game-aware modal copy (EA-invite flow, matchup screen).
  const isMaddenMatch = useCallback(() => {
    return readyStatus?.inviteMode === 'ea_ign'
      || String(readyStatus?.game || readyStatus?.gameName || '').toLowerCase() === 'mdn'
      || String(readyStatus?.gameName || '').toLowerCase().includes('madden');
  }, [readyStatus?.inviteMode, readyStatus?.game, readyStatus?.gameName]);

  // Player-initiated verification re-run (verification_failed state).
  const handleRerunVerification = useCallback(async () => {
    if (!pendingMatch?.path || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const rerun = httpsCallable(functions, 'requestVerificationRetry');
      const result = await rerun({ matchPath: pendingMatch.path });
      console.log('[MatchReady] Verification re-run:', result.data);
      // Backend flips readyStatus back to 'verifying'; the match listener
      // moves the modal to the VERIFYING phase on its own.
    } catch (err) {
      console.error('[MatchReady] Verification re-run failed:', err);
      setError(err.message || 'Could not restart verification. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [pendingMatch?.path, functions, isSubmitting]);

  // Manual-results reporting (games without vision resolution): send the
  // player's win/loss (H2H) or finishing position (grids) to
  // submitMatchResult. Agreement resolves through the normal completion
  // pipeline; the panel's waiting/conflict states come off the match doc.
  const handleSubmitResult = useCallback(async () => {
    if (!pendingMatch?.path || reportSubmitting) return;
    const parts = String(pendingMatch.path).split('/');
    if (parts[0] !== 'tournaments') return;
    const eventId = parts[1];
    const isSubMatch = parts.length === 4;
    const isGrid = !isSubMatch;

    const payload = { eventId };
    if (isGrid) {
      if (reportDnf) {
        payload.dnf = true;
      } else {
        const pos = Math.floor(Number(reportPosition));
        if (!Number.isFinite(pos) || pos < 1) {
          setReportError('Enter your finishing position (1 = first).');
          return;
        }
        payload.position = pos;
      }
    } else {
      if (reportChoice !== 'win' && reportChoice !== 'loss') return;
      payload.matchId = parts[3];
      payload.outcome = reportChoice;
    }

    setReportSubmitting(true);
    setReportError(null);
    try {
      const submitResult = httpsCallable(functions, 'submitMatchResult');
      const result = await submitResult(payload);
      console.log('[MatchReady] Result reported:', result.data);
      setReportEditing(false);
      // Resolved matches flow through the completed-match listener; waiting
      // states render off resultReports from the match snapshot.
    } catch (err) {
      console.error('[MatchReady] Result report failed:', err);
      const msg = String(err.message || '');
      setReportError(msg.includes(':') ? msg.split(':').slice(1).join(':').trim() : (msg || 'Could not send your report. Try again.'));
    } finally {
      setReportSubmitting(false);
    }
  }, [pendingMatch?.path, reportSubmitting, reportChoice, reportPosition, reportDnf, functions]);

  // Timeline bookend: stamp playerEndedAt via markMatchEnded (first tap wins;
  // later taps just add to endReports). The panel then opens result entry.
  const handleMarkEnded = useCallback(async () => {
    if (!pendingMatch?.path || reportSubmitting) return;
    const parts = String(pendingMatch.path).split('/');
    if (parts[0] !== 'tournaments') return;
    setReportSubmitting(true);
    setReportError(null);
    try {
      const markEnded = httpsCallable(functions, 'markMatchEnded');
      await markEnded({ eventId: parts[1], ...(parts.length === 4 ? { matchId: parts[3] } : {}) });
      // playerEndedAt lands via the match snapshot — the panel re-renders
      // into result entry on its own.
    } catch (err) {
      console.error('[MatchReady] Mark-ended failed:', err);
      const msg = String(err.message || '');
      setReportError(msg.includes(':') ? msg.split(':').slice(1).join(':').trim() : (msg || 'Could not mark the match ended. Try again.'));
    } finally {
      setReportSubmitting(false);
    }
  }, [pendingMatch?.path, reportSubmitting, functions]);

  const handleFileDispute = useCallback(async () => {
    // Post-match disputes come from matchResult; under-review disputes happen
    // while the match is still 'live', where only pendingMatch has the path.
    const disputePath = matchResult?.matchPath || pendingMatch?.path;
    if (!disputePath || !selectedDisputeReason || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Backend contract is { matchId, eventId, reason, description } — the
      // old payload sent matchPath/details and every dispute bounced with
      // "matchId and eventId required". Parse ids from the matchPath
      // (tournaments/{eventId}/matches/{matchId}) with result-field fallbacks.
      const pathParts = String(disputePath).split('/');
      const eventId = matchResult?.eventId
        || (pathParts[0] === 'tournaments' ? pathParts[1] : null);
      const matchId = matchResult?.matchId || pathParts[3] || pathParts[pathParts.length - 1];
      const fileDispute = httpsCallable(functions, 'fileDispute');
      const result = await fileDispute({
        matchId,
        eventId,
        reason: selectedDisputeReason,
        description: disputeDetails,
      });
      console.log('[MatchReady] Dispute filed:', result.data);

      // Show success and dismiss
      if (matchResult?.matchPath) acknowledgedMatchesRef.current.add(matchResult.matchPath);
      setHasAcknowledged(true);
      setPhase(PHASE.DISMISSED);
      setPendingMatch(null);
      setMatchResult(null);
    } catch (err) {
      console.error('[MatchReady] Dispute failed:', err);
      setError(err.message || 'Failed to file dispute. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [matchResult?.matchPath, pendingMatch?.path, selectedDisputeReason, disputeDetails, functions, isSubmitting]);

  // Declared BEFORE handleAcceptResult/handleNextRound, which list it in their
  // deps arrays (audit L5): with `const` + useCallback this was a latent TDZ
  // reference that only survived via Babel downleveling — a raised build
  // target would white-screen the whole app (this provider wraps App).
  const resetHostRating = useCallback(() => {
    setHostRatings({});
    setHostRatingFeedback('');
    setHostRatingSubmitted(false);
    setHostRatingSubmitting(false);
  }, []);

  const handleAcceptResult = useCallback(async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Acknowledge the result
      const acknowledgeResult = httpsCallable(functions, 'acknowledgeMatchResult');
      await acknowledgeResult({ matchPath: matchResult?.matchPath });
    } catch (err) {
      // Silently fail - still dismiss the modal
      console.error('[MatchReady] Acknowledge failed:', err);
    }

    if (matchResult?.matchPath) acknowledgedMatchesRef.current.add(matchResult.matchPath);
    setHasAcknowledged(true);
    setPhase(PHASE.DISMISSED);
    setPendingMatch(null);
    setMatchResult(null);
    setIsSubmitting(false);
    resetHostRating();
  }, [matchResult?.matchPath, functions, isSubmitting, resetHostRating]);

  const handleNextRound = useCallback(async () => {
    if (!matchResult?.nextMatchPath) {
      handleAcceptResult();
      return;
    }

    if (matchResult?.matchPath) acknowledgedMatchesRef.current.add(matchResult.matchPath);
    setHasAcknowledged(true);
    setPhase(PHASE.DISMISSED);
    
    // The next match listener will pick up automatically
    setMatchResult(null);
    setPendingMatch(null);
    resetHostRating();
  }, [matchResult?.nextMatchPath, handleAcceptResult, resetHostRating]);

  // ============================================================================
  // HOST RATING HANDLER (Phase 5)
  // (resetHostRating moved above handleAcceptResult — audit L5 TDZ fix)
  // ============================================================================

  const handleSubmitHostRating = useCallback(async () => {
    if (!matchResult?.hostUid || !matchResult?.eventId || !matchResult?.matchId) return;
    if (hostRatingSubmitting || hostRatingSubmitted) return;

    // Need at least the universal categories
    const required = ['punctuality', 'communication', 'overall'];
    const missing = required.filter(k => !hostRatings[k]);
    if (missing.length > 0) return;

    setHostRatingSubmitting(true);
    try {
      const submitRating = httpsCallable(functions, 'submitHostRating');
      await submitRating({
        eventId: matchResult.eventId,
        matchId: matchResult.matchId,
        ratings: hostRatings,
        feedback: hostRatingFeedback.trim() || undefined,
      });
      setHostRatingSubmitted(true);
    } catch (err) {
      console.warn('[MatchReady] Host rating failed:', err.message);
      // Non-fatal — don't block the result flow
      setHostRatingSubmitted(true); // Hide survey anyway
    } finally {
      setHostRatingSubmitting(false);
    }
  }, [matchResult, hostRatings, hostRatingFeedback, functions, hostRatingSubmitting, hostRatingSubmitted]);

  // ============================================================================
  // HELPERS
  // ============================================================================
  
  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  
  const formatDisputeTime = (seconds) => {
    if (seconds === null) return '--:--:--';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const isUserReady = readyStatus?.playersReadyState?.[user?.uid]?.ready;
  const playersReady = readyStatus?.playersReady || 0;
  const totalPlayers = readyStatus?.totalPlayersRequired || 0;

  // ============================================================================
  // GET PHASE-SPECIFIC CONTENT
  // ============================================================================
  
  const getPhaseContent = () => {
    switch (phase) {
      // Post-match phases
      case PHASE.CHAMPION:
        return renderChampion();
      case PHASE.RESULT_WIN:
        return renderResultWin();
      case PHASE.RESULT_LOSS:
        return renderResultLoss();
      case PHASE.UNDER_REVIEW:
        return renderUnderReview();
      case PHASE.NEXT_ROUND:
        return renderNextRound();
        
      // Pre-match phases
      case PHASE.CRIT_TIME:
        return renderCritTime();
      case PHASE.REPORT_RESULT:
        return renderReportResult();
      case PHASE.VERIFYING:
        return renderVerifying();
      case PHASE.VERIFICATION_FAILED:
        return renderVerificationFailed();
      case PHASE.ALL_READY:
        return renderAllReady();
      case PHASE.WAITING_OTHERS:
        return renderWaitingOthers();
      case PHASE.READY_UP:
      default:
        return renderReadyUp();
    }
  };

  // ============================================================================
  // RENDER: CHAMPION (Tournament Winner!)
  // ============================================================================
  
  const renderChampion = () => (
    <Animated.View 
      style={[
        styles.resultContainer,
        {
          transform: [{ scale: resultScale }],
          opacity: resultOpacity,
        }
      ]}
    >
      <View style={[styles.resultGlow, styles.championGlow]} />
      
      <View style={[styles.resultContent, styles.championContent]}>
        <Ionicons name="trophy" size={100} color="#FFD700" style={styles.championEmoji} />
        <Text style={styles.championTitle}>CHAMPION!</Text>
        <Text style={styles.championSubtitle}>
          {matchResult?.racingShowdown ? 'P1 — Race Champion' : 'Tournament Winner'}
        </Text>

        <View style={styles.resultDivider} />

        <Text style={styles.resultEventName}>{matchResult?.eventName}</Text>

        {matchResult?.racingShowdown ? (
          matchResult?.critsReceived > 0 ? (
            <View style={styles.prizeBox}>
              <Text style={styles.prizeLabel}>Crits received</Text>
              {/* Boost is a SECOND value (ceremony treatment, "72 +13") —
                  never folded into the payout number. */}
              <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 6 }}>
                <Text style={styles.prizeAmount}>+{Math.round(matchResult.critsReceived)}</Text>
                {matchResult?.critsBoost > 0 ? (
                  <Text style={styles.prizeBoost}>+{matchResult.critsBoost}</Text>
                ) : null}
                <Text style={styles.prizeCritsLbl}>Crits</Text>
              </View>
            </View>
          ) : null
        ) : (matchResult?.prizeAmount > 0 && (
          <View style={styles.prizeBox}>
            <Text style={styles.prizeLabel}>Your Crits</Text>
            <Text style={styles.prizeAmount}>
              +{Math.round(matchResult.prizeAmount)} Crits
            </Text>
          </View>
        ))}
        {/* Rumbles: money-free, no share figure (see crowdBoosted). */}
        {matchResult?.racingShowdown && matchResult?.crowdBoosted ? (
          <View style={styles.crowdBoostRow}>
            <Ionicons name="flash" size={16} color="#c792ff" style={{ marginRight: 6 }} />
            <Text style={styles.crowdBoostText}>The crowd boosted this race</Text>
          </View>
        ) : null}

        <TouchableOpacity
          style={styles.acceptButton}
          onPress={handleAcceptResult}
          activeOpacity={0.8}
        >
          <View style={styles.buttonContent}>
            <Ionicons name="ribbon" size={20} color="#fff" style={{ marginRight: 8 }} />
            <Text style={styles.acceptButtonText}>CLAIM VICTORY</Text>
          </View>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  // ============================================================================
  // HOST RATING SURVEY (Phase 5)
  //
  // Inline component shown in both win and loss result screens.
  // Only appears when the match had a host (hostUid is set).
  // Universal categories: punctuality, communication, overall.
  // Rating is 1-5 stars for simplicity in mobile UI.
  // ============================================================================

  const RATING_CATEGORIES = [
    { key: 'punctuality', label: 'Punctuality', icon: 'time-outline' },
    { key: 'communication', label: 'Communication', icon: 'chatbubble-outline' },
    { key: 'overall', label: 'Overall', icon: 'star-outline' },
  ];

  const renderHostRatingSurvey = () => {
    // Madden ('mdn') uses direct EA invites — no rated host, so never survey.
    // Madden reuses hostUid to carry the INVITER, so the !hostUid gate below
    // won't catch it — detect Madden via the match's inviteMode (set on every
    // mdn match), with gameId / gameName fallbacks.
    if (matchResult?.inviteMode === 'ea_ign'
        || String(matchResult?.gameId || '').toLowerCase() === 'mdn'
        || String(matchResult?.gameName || '').toLowerCase().includes('madden')) {
      return null;
    }
    // Only show if match had a hosted match (not H2H, has hostUid)
    if (!matchResult?.hostUid) return null;
    // Don't show if user IS the host
    if (matchResult.hostUid === user?.uid) return null;

    if (hostRatingSubmitted) {
      return (
        <View style={styles.hostRatingBox}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Ionicons name="checkmark-circle" size={16} color="#4CAF50" />
            <Text style={{ color: '#4CAF50', fontSize: 12, fontWeight: '600' }}>Host rated</Text>
          </View>
        </View>
      );
    }

    const allFilled = RATING_CATEGORIES.every(c => hostRatings[c.key] >= 2);

    return (
      <View style={styles.hostRatingBox}>
        <Text style={styles.hostRatingTitle}>Rate Your Host</Text>

        {RATING_CATEGORIES.map(cat => (
          <View key={cat.key} style={styles.hostRatingRow}>
            <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
              <Ionicons name={cat.icon} size={14} color="#888" style={{ marginRight: 6 }} />
              <Text style={styles.hostRatingLabel}>{cat.label}</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 4 }}>
              {[1, 2, 3, 4, 5].map(star => {
                const storedVal = hostRatings[cat.key] || 0;
                const filled = storedVal >= star * 2;
                return (
                  <TouchableOpacity
                    key={star}
                    onPress={() => setHostRatings(prev => ({
                      ...prev,
                      // Scale 1-5 stars to 2-10 for backend (star * 2)
                      [cat.key]: star * 2,
                    }))}
                    hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                  >
                    <Ionicons
                      name={filled ? 'star' : 'star-outline'}
                      size={20}
                      color={filled ? '#FCD000' : '#555'}
                    />
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        ))}

        <TouchableOpacity
          style={[
            styles.hostRatingSubmitBtn,
            (!allFilled || hostRatingSubmitting) && { opacity: 0.5 },
          ]}
          onPress={handleSubmitHostRating}
          disabled={!allFilled || hostRatingSubmitting}
        >
          <Text style={styles.hostRatingSubmitText}>
            {hostRatingSubmitting ? 'Sending...' : 'Submit Rating'}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  // ============================================================================
  // RENDER: RESULT WIN (Victory Screen)
  // ============================================================================
  
  const renderResultWin = () => (
    <Animated.View 
      style={[
        styles.resultContainer,
        {
          transform: [{ scale: resultScale }],
          opacity: resultOpacity,
        }
      ]}
    >
      <View style={[styles.resultGlow, styles.winGlow]} />
      
      <View style={[styles.resultContent, styles.winContent]}>
        <Ionicons name="checkmark-circle" size={64} color="#4CAF50" style={styles.resultEmoji} />
        <Text style={styles.resultTitle}>{matchResult?.racing ? 'YOU ADVANCE!' : 'VICTORY!'}</Text>
        <Text style={styles.resultSubtitle}>
          {matchResult?.racing
            ? `P${matchResult?.racingPosition ?? '?'} in your heat — on to the next round`
            : 'You won the match'}
        </Text>

        <View style={styles.resultDivider} />

        {matchResult?.racing ? (
          <View style={styles.matchDetails}>
            <Text style={styles.matchDetailLabel}>Your finish</Text>
            <Text style={styles.matchDetailValue}>P{matchResult?.racingPosition ?? '?'}</Text>
          </View>
        ) : (
          <View style={styles.matchDetails}>
            <Text style={styles.matchDetailLabel}>Defeated</Text>
            <Text style={styles.matchDetailValue}>{matchResult?.opponentName}</Text>
          </View>
        )}

        <View style={styles.matchDetails}>
          <Text style={styles.matchDetailLabel}>Round</Text>
          <Text style={styles.matchDetailValue}>{matchResult?.roundName}</Text>
        </View>
        
        {/* Host Rating Survey (Phase 5) */}
        {renderHostRatingSurvey()}
        
        {matchResult?.nextMatchPath ? (
          <>
            <View style={styles.nextRoundBox}>
              <Text style={styles.nextRoundLabel}>Next Round Starting Soon</Text>
              <Text style={styles.nextRoundHint}>You'll be notified when it's time to ready up</Text>
            </View>
            
            <TouchableOpacity 
              style={styles.nextRoundButton}
              onPress={handleNextRound}
              activeOpacity={0.8}
            >
              <View style={styles.buttonContent}>
                <Ionicons name="checkmark-circle" size={20} color="#fff" style={{ marginRight: 8 }} />
                <Text style={styles.nextRoundButtonText}>GOT IT!</Text>
              </View>
            </TouchableOpacity>
          </>
        ) : (
          <TouchableOpacity 
            style={styles.acceptButton}
            onPress={handleAcceptResult}
            activeOpacity={0.8}
          >
            <View style={styles.buttonContent}>
              <Ionicons name="checkmark-circle" size={20} color="#fff" style={{ marginRight: 8 }} />
              <Text style={styles.acceptButtonText}>CONTINUE</Text>
            </View>
          </TouchableOpacity>
        )}
      </View>
    </Animated.View>
  );

  // ============================================================================
  // RENDER: RESULT LOSS (Dispute Options)
  // ============================================================================
  
  const renderResultLoss = () => (
    <View style={styles.modal}>
      <View style={styles.header}>
        <Text style={[styles.title, styles.lossTitle]}>
          {matchResult?.racingShowdown ? 'RACE COMPLETE' : 'MATCH COMPLETE'}
        </Text>
      </View>

      <View style={styles.lossContent}>
        {/* A paid finish is not a sad face — placement copy below matches. */}
        <Ionicons
          name={matchResult?.racingShowdown && matchResult?.critsReceived > 0
            ? 'trophy-outline'
            : matchResult?.racingShowdown && matchResult?.racingPosition != null
              && !matchResult?.racingDnf && !matchResult?.racingDq
              ? 'flag-outline'
              : 'sad-outline'}
          size={48}
          color={matchResult?.racingShowdown && matchResult?.critsReceived > 0 ? '#FFD700' : '#888'}
          style={styles.lossEmoji}
        />
        {matchResult?.racing ? (
          <>
            {matchResult?.racingShowdown && matchResult?.eventName ? (
              <Text style={styles.resultEventName}>{matchResult.eventName}</Text>
            ) : null}
            <Text style={styles.lossText}>
              {matchResult?.racingShowdown
                ? (matchResult?.racingDq
                  ? 'No-show — you didn’t ready up in time'
                  : matchResult?.racingDnf
                    ? 'DNF — no finish recorded'
                    : matchResult?.racingPosition != null
                      ? (matchResult?.critsReceived > 0
                        ? `P${matchResult.racingPosition} — a paying finish`
                        : `P${matchResult.racingPosition} at the finish`)
                      : 'Race complete')
                : matchResult?.racingDq
                  ? 'No-show — you didn’t ready up in time'
                  : matchResult?.racingFinals
                    ? (matchResult?.racingDnf ? 'DNF in the Finals' : `P${matchResult?.racingPosition ?? '?'} in the Finals`)
                    : matchResult?.racingOnBubble
                      ? `P${matchResult?.racingPosition ?? '?'} — just below the cut`
                      : (matchResult?.racingDnf ? 'DNF — eliminated this round' : `P${matchResult?.racingPosition ?? '?'} — eliminated this round`)}
            </Text>
            <Text style={styles.lossSubtext}>
              {matchResult?.racingShowdown
                ? (matchResult?.racingDq
                  ? 'The race ran without you. Standings are on the event page.'
                  : matchResult?.racingDnf
                    ? 'No finish was recorded for you. Full standings are on the event page.'
                    : matchResult?.critsReceived > 0
                      ? 'Your Crits are already in your balance. Full standings are on the event page.'
                      : 'Thanks for racing — full standings are on the event page.')
                : matchResult?.racingDq
                  ? 'Your heat ran without you. Standings are on the event page.'
                  : matchResult?.racingFinals
                    ? 'Finals are done — final standings are on the event page.'
                    : matchResult?.racingOnBubble
                      ? 'If the round leaves an open seat, the fastest cut time across all heats takes it — watch for a notification.'
                      : 'Thanks for racing — standings are on the event page.'}
            </Text>
            {matchResult?.racingShowdown && matchResult?.critsReceived > 0 ? (
              <View style={styles.critsReceivedRow}>
                <Text style={styles.critsReceivedLabel}>Crits received</Text>
                {/* Boost is a SECOND value (ceremony treatment, "72 +13") —
                    never folded into the payout number. */}
                <Text style={styles.critsReceivedValue}>+{Math.round(matchResult.critsReceived)}</Text>
                {matchResult?.critsBoost > 0 ? (
                  <Text style={styles.critsBoostValue}>+{matchResult.critsBoost}</Text>
                ) : null}
              </View>
            ) : null}
            {/* Rumbles: money-free, no share figure (see crowdBoosted). */}
            {matchResult?.racingShowdown && matchResult?.crowdBoosted ? (
              <View style={styles.crowdBoostRow}>
                <Ionicons name="flash" size={16} color="#c792ff" style={{ marginRight: 6 }} />
                <Text style={styles.crowdBoostText}>The crowd boosted this race</Text>
              </View>
            ) : null}
          </>
        ) : (
          <>
            <Text style={styles.lossText}>Better luck next time</Text>
            <Text style={styles.lossSubtext}>
              {matchResult?.opponentName} won this round
            </Text>
          </>
        )}
      </View>
      
      {/* Host Rating Survey (Phase 5) */}
      {renderHostRatingSurvey()}
      
      {/* Dispute Timer */}
      {disputeTimeLeft !== null && disputeTimeLeft > 0 && (
        <View style={styles.disputeTimerBox}>
          <Text style={styles.disputeTimerLabel}>Dispute Window</Text>
          <Text style={styles.disputeTimerValue}>
            {formatDisputeTime(disputeTimeLeft)}
          </Text>
          <Text style={styles.disputeTimerHint}>
            File a dispute if you believe there was an error
          </Text>
        </View>
      )}
      
      {/* Dispute Reasons */}
      {disputeTimeLeft !== null && disputeTimeLeft > 0 && (
        <View style={styles.disputeSection}>
          <Text style={styles.disputeSectionTitle}>Report an Issue</Text>
          
          <ScrollView style={styles.disputeReasonsList} showsVerticalScrollIndicator={false}>
            {DISPUTE_REASONS.map((reason) => (
              <TouchableOpacity
                key={reason.id}
                style={[
                  styles.disputeReasonItem,
                  selectedDisputeReason === reason.id && styles.disputeReasonSelected,
                ]}
                onPress={() => setSelectedDisputeReason(reason.id)}
                activeOpacity={0.7}
              >
                <View style={styles.disputeReasonIcon}>
                  <Ionicons name={reason.icon} size={20} color={reason.iconColor || '#888'} />
                </View>
                <Text style={[
                  styles.disputeReasonLabel,
                  selectedDisputeReason === reason.id && styles.disputeReasonLabelSelected,
                ]}>
                  {reason.label}
                </Text>
                {selectedDisputeReason === reason.id && (
                  <Ionicons name="checkmark" size={18} color="#FF9800" />
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
          
          {/* File Dispute Button */}
          {selectedDisputeReason && (
            <TouchableOpacity 
              style={[
                styles.disputeButton,
                isSubmitting && styles.buttonDisabled,
              ]}
              onPress={handleFileDispute}
              disabled={isSubmitting}
              activeOpacity={0.8}
            >
              <View style={styles.buttonContent}>
                {!isSubmitting && <Ionicons name="warning" size={18} color="#000" style={{ marginRight: 8 }} />}
                <Text style={styles.disputeButtonText}>
                  {isSubmitting ? 'FILING...' : 'FILE DISPUTE'}
                </Text>
              </View>
            </TouchableOpacity>
          )}
        </View>
      )}
      
      {/* Error Message */}
      {error && (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
      
      {/* Accept Result Button */}
      <TouchableOpacity 
        style={[
          styles.acceptResultButton,
          isSubmitting && styles.buttonDisabled,
        ]}
        onPress={handleAcceptResult}
        disabled={isSubmitting}
        activeOpacity={0.8}
      >
        <View style={styles.buttonContent}>
          {!isSubmitting && <Ionicons name="checkmark-circle" size={18} color="#888" style={{ marginRight: 8 }} />}
          <Text style={styles.acceptResultButtonText}>
            {isSubmitting ? 'Processing...' : 'Accept Result'}
          </Text>
        </View>
      </TouchableOpacity>
      
      <Text style={styles.acceptHint}>
        Results are final once the dispute window closes
      </Text>
    </View>
  );

  // ============================================================================
  // RENDER: UNDER REVIEW (outcome couldn't be auto-verified — claim flow open)
  // ============================================================================

  const renderUnderReview = () => {
    const disputeFiledBy = readyStatus?.dispute?.filedBy;
    const iFiledDispute = disputeFiledBy === user?.uid;
    return (
      <View style={styles.modal}>
        <View style={styles.header}>
          <Text style={styles.title}>MATCH UNDER REVIEW</Text>
        </View>

        <View style={styles.lossContent}>
          <Ionicons name="shield-half-outline" size={48} color="#FF9800" style={styles.lossEmoji} />
          <Text style={styles.lossText}>We couldn't confirm this outcome automatically</Text>
          <Text style={styles.lossSubtext}>
            Our team is reviewing the match. You'll be notified when it's resolved — no action is needed.
          </Text>
        </View>

        {disputeFiledBy ? (
          <View style={styles.disputeTimerBox}>
            <Text style={styles.disputeTimerLabel}>Claim Filed</Text>
            <Text style={styles.disputeTimerHint}>
              {iFiledDispute
                ? 'Your claim was submitted and will be considered in the review.'
                : 'Your opponent filed a claim. Both sides will be considered in the review.'}
            </Text>
          </View>
        ) : (
          <View style={styles.disputeSection}>
            <Text style={styles.disputeSectionTitle}>Know what happened? File a claim</Text>

            <ScrollView style={styles.disputeReasonsList} showsVerticalScrollIndicator={false}>
              {DISPUTE_REASONS.map((reason) => (
                <TouchableOpacity
                  key={reason.id}
                  style={[
                    styles.disputeReasonItem,
                    selectedDisputeReason === reason.id && styles.disputeReasonSelected,
                  ]}
                  onPress={() => setSelectedDisputeReason(reason.id)}
                  activeOpacity={0.7}
                >
                  <View style={styles.disputeReasonIcon}>
                    <Ionicons name={reason.icon} size={20} color={reason.iconColor || '#888'} />
                  </View>
                  <Text style={[
                    styles.disputeReasonLabel,
                    selectedDisputeReason === reason.id && styles.disputeReasonLabelSelected,
                  ]}>
                    {reason.label}
                  </Text>
                  {selectedDisputeReason === reason.id && (
                    <Ionicons name="checkmark" size={18} color="#FF9800" />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>

            {selectedDisputeReason && (
              <TouchableOpacity
                style={[styles.disputeButton, isSubmitting && styles.buttonDisabled]}
                onPress={handleFileDispute}
                disabled={isSubmitting}
                activeOpacity={0.8}
              >
                <View style={styles.buttonContent}>
                  {!isSubmitting && <Ionicons name="warning" size={18} color="#000" style={{ marginRight: 8 }} />}
                  <Text style={styles.disputeButtonText}>
                    {isSubmitting ? 'FILING...' : 'FILE CLAIM'}
                  </Text>
                </View>
              </TouchableOpacity>
            )}
          </View>
        )}

        {error && (
          <View style={styles.errorBox}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        <TouchableOpacity
          style={styles.acceptResultButton}
          onPress={() => setPhase(PHASE.DISMISSED)}
          activeOpacity={0.8}
        >
          <View style={styles.buttonContent}>
            <Ionicons name="checkmark-circle" size={18} color="#888" style={{ marginRight: 8 }} />
            <Text style={styles.acceptResultButtonText}>OK — I'll wait for the review</Text>
          </View>
        </TouchableOpacity>
      </View>
    );
  };

  // ============================================================================
  // RENDER: NEXT ROUND (Advancing)
  // ============================================================================
  
  const renderNextRound = () => (
    <Animated.View 
      style={[
        styles.resultContainer,
        {
          transform: [{ scale: resultScale }],
          opacity: resultOpacity,
        }
      ]}
    >
      <View style={[styles.resultGlow, styles.nextRoundGlow]} />
      
      <View style={[styles.resultContent, styles.nextRoundContent]}>
        <Ionicons name="arrow-forward-circle" size={64} color="#2196F3" style={styles.resultEmoji} />
        <Text style={styles.resultTitle}>ADVANCING!</Text>
        <Text style={styles.resultSubtitle}>Get ready for the next round</Text>
        
        <View style={styles.resultDivider} />
        
        <View style={styles.loadingDots}>
          <Animated.View style={[styles.dot, { opacity: dot1Anim }]} />
          <Animated.View style={[styles.dot, { opacity: dot2Anim }]} />
          <Animated.View style={[styles.dot, { opacity: dot3Anim }]} />
        </View>
        
        <Text style={styles.nextRoundWaitText}>
          Waiting for opponent...
        </Text>
      </View>
    </Animated.View>
  );

  // ============================================================================
  // RENDER: CRIT TIME (Celebration Screen)
  // ============================================================================
  
  const renderCritTime = () => {
    const glowOpacity = glowAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [0.3, 0.8],
    });
    
    return (
      <View style={styles.critTimeContainer}>
        <Animated.View 
          style={[
            styles.critTimeGlow,
            { opacity: glowOpacity }
          ]} 
        />
        
        <Animated.View 
          style={[
            styles.critTimeContent,
            {
              transform: [{ scale: critTimeScale }],
              opacity: critTimeOpacity,
            }
          ]}
        >
          <Ionicons name="flash" size={80} color="#FCD000" style={styles.critTimeEmoji} />
          <Text style={styles.critTimeTitle}>CRIT TIME!</Text>
          <Text style={styles.critTimeSubtitle}>Match is LIVE</Text>
          
          <View style={styles.critTimeDivider} />
          
          <Text style={styles.critTimeGame}>
            {readyStatus?.gameName || readyStatus?.game || 'Good luck!'}
          </Text>
          
          <View style={styles.critTimeHint}>
            <Text style={styles.critTimeHintText}>
              <Ionicons name="game-controller" size={16} color="#FCD000" /> Begin playing now! <Ionicons name="game-controller" size={16} color="#FCD000" />
            </Text>
          </View>
        </Animated.View>
      </View>
    );
  };

  // ============================================================================
  // RENDER: REPORT RESULT (manual-results games — players post the outcome)
  // ============================================================================

  const renderReportResult = () => {
    const parts = String(pendingMatch?.path || '').split('/');
    const isGrid = parts.length === 2;
    const reports = readyStatus?.resultReports || {};
    const myReport = user?.uid ? reports[user.uid] : null;
    // "Opponent reported" means the OTHER SIDE for h2h (a teammate's report
    // is not an opponent's); grids just mean other entrants.
    const otherSide = (() => {
      const p1All = [
        readyStatus?.p1?.userId, readyStatus?.p1?.captainUid,
        ...(Array.isArray(readyStatus?.p1?.rosterUids) ? readyStatus.p1.rosterUids : []),
        ...(Array.isArray(readyStatus?.p1?.roster) ? readyStatus.p1.roster : []),
      ].filter(Boolean);
      return p1All.includes(user?.uid) ? readyStatus?.p2 : readyStatus?.p1;
    })();
    const otherSideIds = [
      otherSide?.userId, otherSide?.captainUid,
      ...(Array.isArray(otherSide?.rosterUids) ? otherSide.rosterUids : []),
      ...(Array.isArray(otherSide?.roster) ? otherSide.roster : []),
    ].filter(Boolean);
    const someoneElseReported = isGrid
      ? Object.keys(reports).some((k) => k !== user?.uid)
      : Object.keys(reports).some((k) => otherSideIds.includes(k));
    const conflict = readyStatus?.resultConflict === true;
    // Timeline bookend (founder 2026-08-12): the first "match ended" tap
    // stamps playerEndedAt — ready -> live -> ended bounds the recorded
    // stream. Result entry opens after the match is marked ended.
    const matchEnded = !!readyStatus?.playerEndedAt;
    const gameLabel = eventInfo?.gameName || readyStatus?.gameName
      || eventInfo?.game || readyStatus?.game || '';

    return (
      <View style={styles.modal}>
        <View style={styles.header}>
          <View style={styles.titleRow}>
            <Ionicons name="flag" size={24} color="#FCD000" style={{ marginRight: 8 }} />
            <Text style={styles.title}>{matchEnded || conflict ? 'REPORT YOUR RESULT' : 'MATCH IN PLAY'}</Text>
          </View>
          <TouchableOpacity onPress={() => setIsMinimized(true)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="remove" size={22} color="#888" />
          </TouchableOpacity>
        </View>

        {!conflict && !matchEnded ? (
          <View style={styles.reportSection}>
            <Text style={styles.reportGame}>{gameLabel ? `${gameLabel} — ` : ''}playing now</Text>
            <Text style={styles.reportSubtext}>
              When play stops, mark the match ended. That stamps the end time on the
              match record — then everyone reports the result.
            </Text>
            {reportError ? <Text style={styles.reportError}>{reportError}</Text> : null}
            <TouchableOpacity
              style={[styles.reportSubmitBtn, reportSubmitting && { opacity: 0.5 }]}
              disabled={reportSubmitting}
              onPress={handleMarkEnded}
            >
              <Text style={styles.reportSubmitText}>
                {reportSubmitting ? 'Marking…' : 'MATCH ENDED'}
              </Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {conflict ? (
          <View style={styles.reportSection}>
            <Ionicons name="shield-half" size={44} color="#FF9800" style={{ alignSelf: 'center', marginBottom: 10 }} />
            <Text style={styles.reportHeadline}>The reports do not match</Text>
            <Text style={styles.reportSubtext}>
              Both sides reported a different result, so CritCash is reviewing the match.
              The result posts as soon as the review is done.
            </Text>
          </View>
        ) : myReport ? (
          <View style={styles.reportSection}>
            <Ionicons name="checkmark-circle" size={44} color="#4CAF50" style={{ alignSelf: 'center', marginBottom: 10 }} />
            <Text style={styles.reportHeadline}>
              {isGrid
                ? (myReport.dnf ? 'Reported: DNF' : `Reported: P${myReport.position}`)
                : (myReport.outcome === 'win' ? 'Reported: you won' : 'Reported: you lost')}
            </Text>
            <Text style={styles.reportSubtext}>
              {isGrid
                ? 'Waiting on the rest of the grid. When every finish matches up, results post automatically.'
                : 'Waiting on your opponent. Matching reports post instantly; an unanswered report settles on its own after the wait window.'}
            </Text>
            <TouchableOpacity style={styles.reportChangeBtn} onPress={() => {
              setReportChoice(null);
              setReportPosition('');
              setReportDnf(false);
              // Re-opens the pickers; a new submit overwrites server-side.
              setReportEditing(true);
            }}>
              <Text style={styles.reportChangeBtnText}>Change my report</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {!conflict && matchEnded && (!myReport || reportEditing) ? (
          <View style={styles.reportSection}>
            <Text style={styles.reportGame}>{gameLabel ? `${gameLabel} — ` : ''}how did it end?</Text>
            {someoneElseReported && !myReport ? (
              <Text style={styles.reportNudge}>
                {isGrid
                  ? 'Finishes are coming in from the grid. Add yours to lock the order in.'
                  : 'Your opponent already reported. Confirm yours to lock the result in.'}
              </Text>
            ) : null}

            {isGrid ? (
              <>
                <Text style={styles.reportSubtext}>Where did you finish?</Text>
                <View style={styles.reportRow}>
                  <TextInput
                    style={styles.reportPositionInput}
                    value={reportPosition}
                    onChangeText={(t) => { setReportPosition(t.replace(/[^0-9]/g, '')); setReportDnf(false); }}
                    placeholder="P#"
                    placeholderTextColor="#666"
                    keyboardType="number-pad"
                    maxLength={2}
                  />
                  <TouchableOpacity
                    style={[styles.reportDnfBtn, reportDnf && styles.reportDnfBtnActive]}
                    onPress={() => { setReportDnf(!reportDnf); setReportPosition(''); }}
                  >
                    <Text style={[styles.reportDnfText, reportDnf && { color: '#0b0b0b' }]}>DNF</Text>
                  </TouchableOpacity>
                </View>
              </>
            ) : (
              <View style={styles.reportRow}>
                <TouchableOpacity
                  style={[styles.reportChoiceBtn, styles.reportWinBtn, reportChoice === 'win' && styles.reportWinBtnActive]}
                  onPress={() => setReportChoice('win')}
                >
                  <Ionicons name="trophy" size={20} color={reportChoice === 'win' ? '#0b0b0b' : '#4CAF50'} />
                  <Text style={[styles.reportChoiceText, reportChoice === 'win' && { color: '#0b0b0b' }]}>I WON</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.reportChoiceBtn, styles.reportLossBtn, reportChoice === 'loss' && styles.reportLossBtnActive]}
                  onPress={() => setReportChoice('loss')}
                >
                  <Ionicons name="flag-outline" size={20} color={reportChoice === 'loss' ? '#0b0b0b' : '#F44336'} />
                  <Text style={[styles.reportChoiceText, reportChoice === 'loss' && { color: '#0b0b0b' }]}>I LOST</Text>
                </TouchableOpacity>
              </View>
            )}

            {reportError ? <Text style={styles.reportError}>{reportError}</Text> : null}

            <TouchableOpacity
              style={[
                styles.reportSubmitBtn,
                (reportSubmitting || (isGrid ? (!reportDnf && !reportPosition) : !reportChoice)) && { opacity: 0.5 },
              ]}
              disabled={reportSubmitting || (isGrid ? (!reportDnf && !reportPosition) : !reportChoice)}
              onPress={handleSubmitResult}
            >
              <Text style={styles.reportSubmitText}>
                {reportSubmitting ? 'Sending…' : 'Confirm report'}
              </Text>
            </TouchableOpacity>
            <Text style={styles.reportFinePrint}>
              Reports are honor-system with receipts: streams are recorded, and a wrong
              result can be disputed and reviewed after it posts.
            </Text>
          </View>
        ) : null}
      </View>
    );
  };

  // ============================================================================
  // RENDER: VERIFYING
  // ============================================================================

  const renderVerifying = () => (
    <View style={styles.modal}>
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Ionicons name="search" size={24} color="#FCD000" style={{ marginRight: 8 }} />
          <Text style={styles.title}>VERIFYING</Text>
        </View>
      </View>
      
      <View style={styles.verifyingContent}>
        <Ionicons name="game-controller" size={48} color="#FCD000" style={styles.verifyingEmoji} />
        <Text style={styles.verifyingText}>Verifying players...</Text>
        <Text style={styles.verifyingSubtext}>
          {isMaddenMatch()
            ? 'We’re scanning your stream for the MATCHUP screen — stay on it!'
            : 'Checking that everyone is in the lobby — stay on the lobby screen'}
        </Text>

        {/* Live wrong-screen alert — written by the verifier after a few missed
            samples (same treatment as the streaming alert in ready-up). */}
        {readyStatus?.verificationLiveStatus?.state === 'wrong_screen' && (
          <View style={{
            flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,152,0,0.15)',
            borderWidth: 1, borderColor: '#FF9800', borderRadius: 8, padding: 10, marginTop: 12,
          }}>
            <Ionicons name="tv" size={18} color="#FF9800" style={{ marginRight: 8 }} />
            <Text style={{ color: '#FFB74D', fontSize: 13, flex: 1, fontWeight: '600' }}>
              {isMaddenMatch()
                ? 'We can’t see the MATCHUP screen on the stream — switch to it now! Still scanning…'
                : 'We can’t see the game lobby on the stream — switch to it now! Still scanning…'}
            </Text>
          </View>
        )}

        <View style={styles.loadingDots}>
          <Animated.View style={[styles.dot, { opacity: dot1Anim }]} />
          <Animated.View style={[styles.dot, { opacity: dot2Anim }]} />
          <Animated.View style={[styles.dot, { opacity: dot3Anim }]} />
        </View>
      </View>

      {renderPlayerList()}
    </View>
  );

  // ============================================================================
  // RENDER: VERIFICATION FAILED — offer a player-initiated re-run
  // ============================================================================

  const renderVerificationFailed = () => {
    const retriesUsed = Number(readyStatus?.playerVerifyRetries) || 0;
    const retriesLeft = Math.max(0, 2 - retriesUsed);
    return (
      <View style={styles.modal}>
        <View style={styles.header}>
          <View style={styles.titleRow}>
            <Ionicons name="alert-circle" size={24} color="#FF9800" style={{ marginRight: 8 }} />
            <Text style={styles.title}>VERIFICATION FAILED</Text>
          </View>
        </View>

        <View style={styles.verifyingContent}>
          <Ionicons name="tv" size={48} color="#FF9800" style={styles.verifyingEmoji} />
          <Text style={styles.verifyingText}>
            {isMaddenMatch()
              ? 'We couldn’t see the MATCHUP screen on the stream'
              : 'We couldn’t confirm the game lobby on the stream'}
          </Text>
          <Text style={styles.verifyingSubtext}>
            {isMaddenMatch()
              ? 'Send/accept the EA invite, get BOTH players on the matchup screen, then re-run verification.'
              : 'Get everyone into the lobby on stream, then re-run verification.'}
          </Text>

          {retriesLeft > 0 ? (
            <TouchableOpacity
              style={[styles.readyButton, isSubmitting && styles.readyButtonDisabled, { marginTop: 16 }]}
              onPress={handleRerunVerification}
              disabled={isSubmitting}
              activeOpacity={0.8}
            >
              <View style={styles.buttonContent}>
                <Ionicons name="refresh" size={20} color="#000" style={{ marginRight: 8 }} />
                <Text style={styles.readyButtonText}>
                  {isSubmitting ? 'RESTARTING…' : `RE-RUN VERIFICATION (${retriesLeft} left)`}
                </Text>
              </View>
            </TouchableOpacity>
          ) : (
            <Text style={[styles.verifyingSubtext, { marginTop: 16, color: '#FF9800' }]}>
              Re-run limit reached — an admin will review this match shortly.
            </Text>
          )}

          {error && (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}
        </View>

        {renderPlayerList()}
      </View>
    );
  };

  // ============================================================================
  // RENDER: ALL READY
  // ============================================================================
  
  const renderAllReady = () => (
    <View style={styles.modal}>
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Ionicons name="checkmark-circle" size={24} color="#4CAF50" style={{ marginRight: 8 }} />
          <Text style={styles.title}>ALL READY!</Text>
        </View>
      </View>
      
      <View style={styles.allReadyContent}>
        <Ionicons name="game-controller" size={48} color="#4CAF50" style={styles.allReadyEmoji} />
        <Text style={styles.allReadyText}>All players ready!</Text>
        <Text style={styles.allReadySubtext}>
          Starting verification...
        </Text>
        
        <View style={[styles.loadingDots, { marginTop: 12 }]}>
          <Animated.View style={[styles.dot, { opacity: dot1Anim }]} />
          <Animated.View style={[styles.dot, { opacity: dot2Anim }]} />
          <Animated.View style={[styles.dot, { opacity: dot3Anim }]} />
        </View>
      </View>
      
      {renderPlayerList()}
    </View>
  );

  // ============================================================================
  // RENDER: WAITING FOR OTHERS
  // ============================================================================
  
  const renderWaitingOthers = () => (
    <Animated.View 
      style={[
        styles.modal,
        { transform: [{ scale: pulseAnim }] }
      ]}
    >
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Ionicons name="time" size={24} color="#FCD000" style={{ marginRight: 8 }} />
          <Text style={styles.title}>WAITING</Text>
        </View>
        <TouchableOpacity 
          onPress={() => setIsMinimized(true)}
          style={styles.minimizeButton}
        >
          <Ionicons name="chevron-down" size={20} color="#888" />
        </TouchableOpacity>
      </View>

      <View style={styles.countdown}>
        <Text style={styles.countdownLabel}>Time Remaining</Text>
        <Text style={[
          styles.countdownTime,
          timeLeft !== null && timeLeft < 60 && styles.countdownUrgent
        ]}>
          {formatTime(timeLeft)}
        </Text>
      </View>

      <View style={styles.waitingContent}>
        <Ionicons name="checkmark-circle" size={48} color="#4CAF50" style={styles.waitingEmoji} />
        <Text style={styles.waitingText}>You're ready!</Text>
        <Text style={styles.waitingSubtext}>
          Waiting for opponent to ready up...
        </Text>
        
        <View style={[styles.loadingDots, { marginTop: 12 }]}>
          <Animated.View style={[styles.dot, { opacity: dot1Anim }]} />
          <Animated.View style={[styles.dot, { opacity: dot2Anim }]} />
          <Animated.View style={[styles.dot, { opacity: dot3Anim }]} />
        </View>
      </View>

      {renderPlayerList()}
    </Animated.View>
  );

  // ============================================================================
  // RENDER: READY UP (Initial State)
  // ============================================================================
  
  const renderReadyUp = () => (
    <Animated.View 
      style={[
        styles.modal,
        { transform: [{ scale: pulseAnim }] }
      ]}
    >
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Ionicons name="game-controller" size={24} color="#FCD000" style={{ marginRight: 8 }} />
          <Text style={styles.title}>MATCH READY</Text>
        </View>
        <TouchableOpacity 
          onPress={() => setIsMinimized(true)}
          style={styles.minimizeButton}
        >
          <Ionicons name="chevron-down" size={20} color="#888" />
        </TouchableOpacity>
      </View>

      <View style={styles.countdown}>
        <Text style={styles.countdownLabel}>Time to Ready Up</Text>
        <Text style={[
          styles.countdownTime,
          timeLeft !== null && timeLeft < 60 && styles.countdownUrgent
        ]}>
          {formatTime(timeLeft)}
        </Text>
      </View>

      <View style={styles.matchInfo}>
        <Text style={styles.gameName}>
          {readyStatus?.gameName || readyStatus?.game || 'Match'}
        </Text>
        {String(readyStatus?.game || readyStatus?.gameName || '').toLowerCase() === 'gt7' ? (
          // GT7 (post-Spec III): hosts can't name lobbies or set passwords.
          // Show the auto-generated Room ID; if it isn't here yet, surface a
          // wait state instead of misleading "N/A" credentials.
          // (Check both 'game' and 'gameName' — onEventSeeded.js writes the
          // latter to match docs, but other paths may write 'game'.)
          <View style={styles.lobbyDetails}>
            <Text style={styles.lobbyLabel}>Room ID:</Text>
            <Text style={styles.lobbyValue}>
              {readyStatus?.roomId || 'Waiting for host…'}
            </Text>
          </View>
        ) : isMaddenMatch() ? (
          // Madden has no lobby name/password — it's a direct EA invite. The
          // old generic branch showed "Lobby: N/A / Password: N/A" here, which
          // read as broken. Show who invites whom instead.
          <View style={styles.lobbyDetails}>
            <Text style={styles.lobbyLabel}>EA Invite:</Text>
            <Text style={styles.lobbyValue}>
              {(() => {
                const prs = readyStatus?.playersReadyState || {};
                const opp = Object.entries(prs).find(([u]) => u !== user?.uid)?.[1];
                const inviter = readyStatus?.inviterUserId || readyStatus?.hostUserId || readyStatus?.hostUid;
                return inviter && user?.uid === inviter
                  ? `You invite ${opp?.ign || 'your opponent'} on EA`
                  : `${opp?.ign || 'Your opponent'} sends you the invite`;
              })()}
            </Text>
          </View>
        ) : (
          <>
            <View style={styles.lobbyDetails}>
              <Text style={styles.lobbyLabel}>Lobby:</Text>
              <Text style={styles.lobbyValue}>{readyStatus?.lobbyName || 'N/A'}</Text>
            </View>
            <View style={styles.lobbyDetails}>
              <Text style={styles.lobbyLabel}>Password:</Text>
              <Text style={styles.lobbyValue}>{readyStatus?.lobbyPassword || 'N/A'}</Text>
            </View>
          </>
        )}
      </View>

      {/* Pre-ready instructions (founder 2026-07-04): verification SCANS THE
          STREAM right after everyone readies — players must already be on the
          screen it looks for. Bake the steps into the modal itself. */}
      <View style={{
        backgroundColor: 'rgba(252,208,0,0.08)', borderWidth: 1,
        borderColor: 'rgba(252,208,0,0.35)', borderRadius: 8, padding: 10, marginBottom: 10,
      }}>
        <Text style={{ color: '#FCD000', fontSize: 12, fontWeight: '700', marginBottom: 4 }}>
          BEFORE YOU PRESS READY
        </Text>
        {isMaddenMatch() ? (
          <>
            <Text style={{ color: '#ddd', fontSize: 12, lineHeight: 17 }}>
              1. Send / accept the EA invite (check your invite notification){'\n'}
              2. Get BOTH players to the MATCHUP screen{'\n'}
              3. Stay streaming — we verify by scanning your stream{'\n'}
              4. THEN press READY and stay on the matchup screen
            </Text>
          </>
        ) : (
          <Text style={{ color: '#ddd', fontSize: 12, lineHeight: 17 }}>
            Join the lobby, stay streaming, and keep the lobby on screen — we
            verify by scanning your stream right after everyone readies up.
          </Text>
        )}
      </View>

      {renderPlayerList()}

      {error && (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      <TouchableOpacity
        style={[
          styles.readyButton,
          isUserReady && styles.readyButtonDone,
          isSubmitting && styles.readyButtonDisabled,
        ]}
        onPress={handleReady}
        disabled={isUserReady || isSubmitting}
        activeOpacity={0.8}
      >
        <View style={styles.buttonContent}>
          {isUserReady && <Ionicons name="checkmark" size={20} color="#000" style={{ marginRight: 8 }} />}
          <Text style={[
            styles.readyButtonText,
            isUserReady && styles.readyButtonTextDone
          ]}>
            {isSubmitting 
              ? 'CONFIRMING...' 
              : isUserReady 
                ? 'YOU ARE READY'
                : "I'M READY!"}
          </Text>
        </View>
      </TouchableOpacity>

      <View style={styles.warningRow}>
        <Ionicons name="warning" size={16} color="#FF9800" style={{ marginRight: 4 }} />
        <Ionicons name="game-controller" size={16} color="#FF9800" style={{ marginRight: 8 }} />
        <Text style={styles.warning}>
          If you don't ready up in time, you will forfeit the match
        </Text>
      </View>
    </Animated.View>
  );

  // ============================================================================
  // RENDER: PLAYER LIST (Shared Component)
  // ============================================================================
  
  const renderPlayerList = () => {
    const roster = Object.entries(readyStatus?.playersReadyState || {});
    // The list used to be a fixed 150px scroller, which showed about four of a
    // 16-racer grid and hid the rest behind a scrollbar nobody notices while
    // they're waiting to ready up. Every entrant is shown instead; past a
    // handful the rows go two-up so a full grid still fits without scrolling.
    const twoColumn = roster.length > 6;

    return (
      <View style={styles.playersStatus}>
        <Text style={styles.playersCount}>
          {playersReady} / {totalPlayers} Players Ready
        </Text>

        <View style={styles.progressBar}>
          <View
            style={[
              styles.progressFill,
              { width: totalPlayers > 0 ? `${(playersReady / totalPlayers) * 100}%` : '0%' }
            ]}
          />
        </View>

        {roster.length > 0 && (
          <View style={[styles.playerList, twoColumn && styles.playerListGrid]}>
            {roster.map(([odisplayName, state]) => (
              <View
                key={odisplayName}
                style={[styles.playerRow, twoColumn && styles.playerRowCompact]}
              >
                <Text
                  style={[
                    styles.playerName,
                    twoColumn && styles.playerNameCompact,
                    odisplayName === user?.uid && styles.playerNameYou
                  ]}
                  numberOfLines={1}
                >
                  {state.displayName || state.ign || 'Player'}
                  {odisplayName === user?.uid && ' (You)'}
                </Text>
                {state.ready
                  ? <Ionicons name="checkmark-circle" size={18} color="#4CAF50" />
                  : <Ionicons name="time-outline" size={18} color="#888" />
                }
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  
  const showModal = (pendingMatch || matchResult) && phase && phase !== PHASE.DISMISSED;
  const isResultPhase = [PHASE.RESULT_WIN, PHASE.RESULT_LOSS, PHASE.CHAMPION, PHASE.NEXT_ROUND].includes(phase);
  
  return (
    <MatchReadyContext.Provider value={{ pendingMatch, isReady: isUserReady, phase, matchResult }}>
      {children}
      
      {/* Minimized Banner */}
      {showModal && isMinimized && !isResultPhase && phase !== PHASE.CRIT_TIME && (
        <TouchableOpacity 
          style={styles.minimizedBanner} 
          onPress={() => setIsMinimized(false)}
          activeOpacity={0.8}
        >
          <View style={styles.minimizedContent}>
            {phase === PHASE.REPORT_RESULT ? (
              <>
                <Ionicons name="flag" size={18} color="#FCD000" style={{ marginRight: 8 }} />
                <Text style={styles.minimizedText}>
                  {readyStatus?.resultConflict
                    ? 'Result under review'
                    : (readyStatus?.resultReports?.[user?.uid]
                      ? 'Result reported — waiting on the other side'
                      : (readyStatus?.playerEndedAt
                        ? 'Match ended — report your result'
                        : 'Match over? Tap to end and report'))}
                </Text>
              </>
            ) : phase === PHASE.WAITING_OTHERS ? (
              <>
                <Ionicons name="checkmark-circle" size={18} color="#4CAF50" style={{ marginRight: 8 }} />
                <Text style={styles.minimizedText}>
                  Ready! Waiting for opponent... {formatTime(timeLeft)}
                </Text>
              </>
            ) : (
              <>
                <Ionicons name="warning" size={18} color="#FF9800" style={{ marginRight: 4 }} />
                <Ionicons name="game-controller" size={18} color="#FCD000" style={{ marginRight: 8 }} />
                <Text style={styles.minimizedText}>
                  READY UP! {formatTime(timeLeft)} - Tap to expand
                </Text>
              </>
            )}
          </View>
        </TouchableOpacity>
      )}

      {/* Full Overlay Modal. Result phases ALWAYS take the screen — the
          REPORT_RESULT chip leaves isMinimized true, and a win/loss modal
          that respects it would never be seen. */}
      <Modal
        visible={showModal && (!isMinimized || isResultPhase)}
        transparent
        animationType={phase === PHASE.CRIT_TIME || isResultPhase ? 'none' : 'fade'}
        statusBarTranslucent
      >
        <View style={[
          styles.overlay,
          phase === PHASE.CRIT_TIME && styles.critTimeOverlay,
          isResultPhase && styles.resultOverlay,
        ]}>
          {getPhaseContent()}
        </View>
      </Modal>
    </MatchReadyContext.Provider>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  // Overlay background
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  critTimeOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
  },
  resultOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
  },
  
  // Modal container
  modal: {
    backgroundColor: '#1a1a1a',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 420,
    borderWidth: 2,
    borderColor: '#FFD700',
    ...Platform.select({
      web: {
        boxShadow: '0 0 40px rgba(255, 215, 0, 0.3)',
      },
      default: {
        shadowColor: '#FFD700',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
        elevation: 20,
      },
    }),
  },
  
  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFD700',
  },
  lossTitle: {
    color: '#888',
  },
  minimizeButton: {
    padding: 8,
  },
  minimizeIcon: {
    fontSize: 18,
    color: '#888',
  },
  
  // New styles for Ionicons integration
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  // Result reporting (manual-results games)
  reportSection: { marginBottom: 12 },
  reportHeadline: { color: '#fff', fontSize: 17, fontWeight: '800', textAlign: 'center' },
  reportSubtext: { color: '#999', fontSize: 13, textAlign: 'center', marginTop: 8, lineHeight: 19 },
  reportGame: { color: '#FCD000', fontSize: 14, fontWeight: '800', textAlign: 'center', marginBottom: 4 },
  reportNudge: { color: '#FFB74D', fontSize: 12.5, textAlign: 'center', marginBottom: 6, fontWeight: '600' },
  reportRow: { flexDirection: 'row', justifyContent: 'center', gap: 12, marginTop: 14 },
  reportChoiceBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    borderRadius: 10, borderWidth: 2, paddingVertical: 14, paddingHorizontal: 22,
    minWidth: 130, gap: 8,
  },
  reportWinBtn: { borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.08)' },
  reportWinBtnActive: { backgroundColor: '#4CAF50' },
  reportLossBtn: { borderColor: '#F44336', backgroundColor: 'rgba(244,67,54,0.08)' },
  reportLossBtnActive: { backgroundColor: '#F44336' },
  reportChoiceText: { color: '#fff', fontSize: 15, fontWeight: '800' },
  reportPositionInput: {
    backgroundColor: '#111', borderColor: '#333', borderWidth: 2, borderRadius: 10,
    color: '#fff', fontSize: 22, fontWeight: '800', textAlign: 'center',
    width: 90, paddingVertical: 12,
  },
  reportDnfBtn: {
    borderColor: '#888', borderWidth: 2, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center', paddingHorizontal: 22,
  },
  reportDnfBtnActive: { backgroundColor: '#FF9800', borderColor: '#FF9800' },
  reportDnfText: { color: '#ccc', fontSize: 15, fontWeight: '800' },
  reportSubmitBtn: {
    backgroundColor: '#FCD000', borderRadius: 10, paddingVertical: 14,
    alignItems: 'center', marginTop: 16,
  },
  reportSubmitText: { color: '#0b0b0b', fontSize: 15, fontWeight: '800' },
  reportChangeBtn: { alignSelf: 'center', marginTop: 12, padding: 6 },
  reportChangeBtnText: { color: '#888', fontSize: 13, textDecorationLine: 'underline' },
  reportError: { color: '#F44336', fontSize: 12.5, textAlign: 'center', marginTop: 10 },
  reportFinePrint: { color: '#666', fontSize: 11.5, textAlign: 'center', marginTop: 12, lineHeight: 16 },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  warningRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
  },
  minimizedContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // Countdown
  countdown: {
    alignItems: 'center',
    marginBottom: 24,
  },
  countdownLabel: {
    color: '#888',
    fontSize: 14,
    marginBottom: 4,
  },
  countdownTime: {
    fontSize: 56,
    fontWeight: 'bold',
    color: '#fff',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  countdownUrgent: {
    color: '#ff4444',
  },
  
  // Match info
  matchInfo: {
    backgroundColor: '#2a2a2a',
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  gameName: {
    color: '#FFD700',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
  },
  lobbyDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  lobbyLabel: {
    color: '#888',
    fontSize: 14,
  },
  lobbyValue: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  
  // Players status
  playersStatus: {
    marginBottom: 20,
  },
  playersCount: {
    color: '#FFD700',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 8,
  },
  progressBar: {
    height: 8,
    backgroundColor: '#333',
    borderRadius: 4,
    marginBottom: 12,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#FFD700',
    borderRadius: 4,
  },
  playerList: {
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    padding: 12,
    // No maxHeight — the whole field is visible. See renderPlayerList.
  },
  // Large rosters (full-grid FFA) lay out two-up so all 16 fit on screen.
  playerListGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  playerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  playerRowCompact: {
    width: '50%',
    paddingVertical: 5,
    paddingRight: 12,
    borderBottomWidth: 0,
  },
  playerName: {
    color: '#ccc',
    fontSize: 14,
  },
  playerNameCompact: {
    flex: 1,
    fontSize: 13,
    marginRight: 6,
  },
  playerNameYou: {
    color: '#FFD700',
    fontWeight: 'bold',
  },
  readyYes: {
    fontSize: 18,
  },
  readyNo: {
    fontSize: 18,
  },
  
  // Error
  errorBox: {
    backgroundColor: 'rgba(255, 68, 68, 0.2)',
    borderWidth: 1,
    borderColor: '#ff4444',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  errorText: {
    color: '#ff4444',
    fontSize: 14,
    textAlign: 'center',
  },
  
  // Ready button
  readyButton: {
    backgroundColor: '#FFD700',
    paddingVertical: 18,
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: 16,
  },
  readyButtonDone: {
    backgroundColor: '#2d5a27',
  },
  readyButtonDisabled: {
    opacity: 0.7,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  readyButtonText: {
    color: '#000',
    fontSize: 20,
    fontWeight: 'bold',
  },
  readyButtonTextDone: {
    color: '#fff',
  },
  
  // Warning
  warning: {
    color: '#ff6b6b',
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 18,
  },
  
  // Minimized banner
  minimizedBanner: {
    position: 'absolute',
    top: Platform.OS === 'web' ? 10 : 50,
    left: 20,
    right: 20,
    backgroundColor: '#FFD700',
    padding: 16,
    borderRadius: 12,
    zIndex: 9999,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 10,
      },
    }),
  },
  minimizedText: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  
  // ============================================================================
  // WAITING FOR OTHERS
  // ============================================================================
  waitingContent: {
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 16,
  },
  waitingEmoji: {
    fontSize: 48,
    marginBottom: 12,
  },
  waitingText: {
    color: '#4CAF50',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  waitingSubtext: {
    color: '#888',
    fontSize: 14,
    textAlign: 'center',
  },
  
  // ============================================================================
  // ALL READY
  // ============================================================================
  allReadyContent: {
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 16,
  },
  allReadyEmoji: {
    fontSize: 48,
    marginBottom: 12,
  },
  allReadyText: {
    color: '#4CAF50',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  allReadySubtext: {
    color: '#888',
    fontSize: 14,
    textAlign: 'center',
  },
  
  // ============================================================================
  // VERIFYING
  // ============================================================================
  verifyingContent: {
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 16,
  },
  verifyingEmoji: {
    fontSize: 48,
    marginBottom: 12,
  },
  verifyingText: {
    color: '#FFD700',
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  verifyingSubtext: {
    color: '#888',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
  },
  loadingDots: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#FFD700',
  },
  
  // ============================================================================
  // CRIT TIME - Celebration Screen
  // ============================================================================
  critTimeContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  critTimeGlow: {
    position: 'absolute',
    width: 400,
    height: 400,
    borderRadius: 200,
    backgroundColor: '#FFD700',
    ...Platform.select({
      web: {
        boxShadow: '0 0 100px 50px rgba(255, 215, 0, 0.4)',
      },
      default: {
        shadowColor: '#FFD700',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.5,
        shadowRadius: 100,
      },
    }),
  },
  critTimeContent: {
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    borderRadius: 24,
    padding: 40,
    borderWidth: 3,
    borderColor: '#FFD700',
    ...Platform.select({
      web: {
        boxShadow: '0 0 60px rgba(255, 215, 0, 0.5)',
      },
      default: {
        shadowColor: '#FFD700',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.5,
        shadowRadius: 30,
        elevation: 30,
      },
    }),
  },
  critTimeEmoji: {
    fontSize: 80,
    marginBottom: 16,
  },
  critTimeTitle: {
    fontSize: 48,
    fontWeight: '900',
    color: '#FFD700',
    letterSpacing: 4,
    textAlign: 'center',
    ...Platform.select({
      web: {
        textShadow: '0 0 20px rgba(255, 215, 0, 0.8)',
      },
    }),
  },
  critTimeSubtitle: {
    fontSize: 20,
    color: '#4CAF50',
    fontWeight: 'bold',
    marginTop: 8,
    letterSpacing: 2,
  },
  critTimeDivider: {
    width: 100,
    height: 3,
    backgroundColor: '#FFD700',
    marginVertical: 20,
    borderRadius: 2,
  },
  critTimeGame: {
    fontSize: 18,
    color: '#fff',
    fontWeight: '600',
    marginBottom: 20,
  },
  critTimeHint: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#4CAF50',
  },
  critTimeHintText: {
    color: '#4CAF50',
    fontSize: 16,
    fontWeight: 'bold',
    letterSpacing: 1,
  },
  
  // ============================================================================
  // RESULT SCREENS (WIN/LOSS/CHAMPION)
  // ============================================================================
  resultContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  resultGlow: {
    position: 'absolute',
    width: 400,
    height: 400,
    borderRadius: 200,
  },
  winGlow: {
    backgroundColor: '#4CAF50',
    ...Platform.select({
      web: {
        boxShadow: '0 0 100px 50px rgba(76, 175, 80, 0.4)',
      },
      default: {
        shadowColor: '#4CAF50',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.5,
        shadowRadius: 100,
      },
    }),
  },
  championGlow: {
    backgroundColor: '#FFD700',
    ...Platform.select({
      web: {
        boxShadow: '0 0 150px 75px rgba(255, 215, 0, 0.5)',
      },
      default: {
        shadowColor: '#FFD700',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.6,
        shadowRadius: 150,
      },
    }),
  },
  nextRoundGlow: {
    backgroundColor: '#2196F3',
    ...Platform.select({
      web: {
        boxShadow: '0 0 100px 50px rgba(33, 150, 243, 0.4)',
      },
      default: {
        shadowColor: '#2196F3',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.5,
        shadowRadius: 100,
      },
    }),
  },
  resultContent: {
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    borderRadius: 24,
    padding: 40,
    borderWidth: 3,
    width: '100%',
    maxWidth: 420,
  },
  winContent: {
    borderColor: '#4CAF50',
    ...Platform.select({
      web: {
        boxShadow: '0 0 60px rgba(76, 175, 80, 0.5)',
      },
      default: {
        shadowColor: '#4CAF50',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.5,
        shadowRadius: 30,
        elevation: 30,
      },
    }),
  },
  championContent: {
    borderColor: '#FFD700',
    ...Platform.select({
      web: {
        boxShadow: '0 0 80px rgba(255, 215, 0, 0.6)',
      },
      default: {
        shadowColor: '#FFD700',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.6,
        shadowRadius: 40,
        elevation: 40,
      },
    }),
  },
  nextRoundContent: {
    borderColor: '#2196F3',
    ...Platform.select({
      web: {
        boxShadow: '0 0 60px rgba(33, 150, 243, 0.5)',
      },
      default: {
        shadowColor: '#2196F3',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.5,
        shadowRadius: 30,
        elevation: 30,
      },
    }),
  },
  resultEmoji: {
    fontSize: 80,
    marginBottom: 16,
  },
  resultTitle: {
    fontSize: 40,
    fontWeight: '900',
    color: '#4CAF50',
    letterSpacing: 3,
    textAlign: 'center',
  },
  resultSubtitle: {
    fontSize: 18,
    color: '#888',
    marginTop: 8,
  },
  resultDivider: {
    width: 100,
    height: 3,
    backgroundColor: '#4CAF50',
    marginVertical: 20,
    borderRadius: 2,
  },
  resultEventName: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '600',
    marginBottom: 16,
  },
  matchDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingHorizontal: 20,
    marginBottom: 8,
  },
  matchDetailLabel: {
    color: '#888',
    fontSize: 14,
  },
  matchDetailValue: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  
  // Champion specific
  championEmoji: {
    fontSize: 100,
    marginBottom: 16,
  },
  championTitle: {
    fontSize: 48,
    fontWeight: '900',
    color: '#FFD700',
    letterSpacing: 4,
    textAlign: 'center',
    ...Platform.select({
      web: {
        textShadow: '0 0 30px rgba(255, 215, 0, 0.8)',
      },
    }),
  },
  championSubtitle: {
    fontSize: 20,
    color: '#4CAF50',
    fontWeight: 'bold',
    marginTop: 8,
  },
  prizeBox: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    borderWidth: 1,
    borderColor: '#4CAF50',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 20,
    width: '100%',
  },
  prizeLabel: {
    color: '#4CAF50',
    fontSize: 14,
    marginBottom: 4,
  },
  prizeAmount: {
    color: '#4CAF50',
    fontSize: 32,
    fontWeight: 'bold',
  },
  // Rumble boost as a SECOND value beside the payout ("72 +13") — King Crit
  // purple, matching the ceremony's critsBoost treatment.
  prizeBoost: {
    color: '#c792ff',
    fontSize: 24,
    fontWeight: 'bold',
  },
  prizeCritsLbl: {
    color: '#4CAF50',
    fontSize: 16,
    fontWeight: '600',
  },
  // Showdown (event-level FFA) result: Crits received on the RACE COMPLETE
  // screen for non-champion paid finishers.
  critsReceivedRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
    marginTop: 12,
  },
  critsReceivedLabel: {
    color: '#888',
    fontSize: 14,
  },
  critsReceivedValue: {
    color: '#FCD000',
    fontSize: 22,
    fontWeight: '900',
  },
  critsBoostValue: {
    color: '#c792ff',
    fontSize: 18,
    fontWeight: '800',
  },
  // Rumble line (evergreen 3.3): money-free, same King Crit purple.
  crowdBoostRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
  },
  crowdBoostText: {
    color: '#c792ff',
    fontSize: 14,
    fontWeight: '700',
  },

  // Next round
  nextRoundBox: {
    backgroundColor: 'rgba(33, 150, 243, 0.2)',
    borderWidth: 1,
    borderColor: '#2196F3',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 20,
    width: '100%',
  },
  nextRoundLabel: {
    color: '#2196F3',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  nextRoundHint: {
    color: '#888',
    fontSize: 12,
    textAlign: 'center',
  },
  nextRoundButton: {
    backgroundColor: '#2196F3',
    paddingVertical: 16,
    paddingHorizontal: 40,
    borderRadius: 12,
    width: '100%',
    alignItems: 'center',
  },
  nextRoundButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  nextRoundWaitText: {
    color: '#888',
    fontSize: 14,
    marginTop: 16,
  },
  acceptButton: {
    backgroundColor: '#4CAF50',
    paddingVertical: 16,
    paddingHorizontal: 40,
    borderRadius: 12,
    width: '100%',
    alignItems: 'center',
  },
  acceptButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  
  // ============================================================================
  // LOSS SCREEN
  // ============================================================================
  lossContent: {
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 16,
  },
  lossEmoji: {
    fontSize: 48,
    marginBottom: 12,
  },
  lossText: {
    color: '#888',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  lossSubtext: {
    color: '#666',
    fontSize: 14,
    textAlign: 'center',
  },
  
  // Dispute timer
  disputeTimerBox: {
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    borderWidth: 1,
    borderColor: '#FF9800',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    marginBottom: 16,
  },
  disputeTimerLabel: {
    color: '#FF9800',
    fontSize: 12,
    marginBottom: 4,
  },
  disputeTimerValue: {
    color: '#FF9800',
    fontSize: 28,
    fontWeight: 'bold',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  disputeTimerHint: {
    color: '#888',
    fontSize: 11,
    marginTop: 4,
    textAlign: 'center',
  },
  
  // Dispute section
  disputeSection: {
    marginBottom: 16,
  },
  disputeSectionTitle: {
    color: '#888',
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  disputeReasonsList: {
    maxHeight: 200,
  },
  disputeReasonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2a2a2a',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#333',
  },
  disputeReasonSelected: {
    borderColor: '#FF9800',
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
  },
  disputeReasonIcon: {
    width: 32,
    alignItems: 'center',
    marginRight: 12,
  },
  disputeReasonLabel: {
    color: '#888',
    fontSize: 14,
    flex: 1,
  },
  disputeReasonLabelSelected: {
    color: '#FF9800',
  },
  disputeReasonCheck: {
    color: '#FF9800',
    fontSize: 18,
    fontWeight: 'bold',
  },
  disputeButton: {
    backgroundColor: '#FF9800',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 8,
  },
  disputeButtonText: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
  },
  
  // Accept result
  acceptResultButton: {
    backgroundColor: '#333',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginBottom: 8,
  },
  acceptResultButtonText: {
    color: '#888',
    fontSize: 16,
    fontWeight: '600',
  },
  acceptHint: {
    color: '#555',
    fontSize: 11,
    textAlign: 'center',
  },

  // Host Rating Survey styles (Phase 5)
  hostRatingBox: {
    backgroundColor: '#1a1a1a',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#333',
    padding: 12,
    marginTop: 12,
    marginBottom: 4,
  },
  hostRatingTitle: {
    color: '#FCD000',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 10,
    letterSpacing: 0.3,
  },
  hostRatingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },
  hostRatingLabel: {
    color: '#ccc',
    fontSize: 12,
    fontWeight: '500',
  },
  hostRatingSubmitBtn: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 8,
    alignItems: 'center',
    marginTop: 10,
  },
  hostRatingSubmitText: {
    color: '#000',
    fontSize: 12,
    fontWeight: '800',
  },
});

export default MatchReadyProvider;